import {
  Box, Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput, NumberInputField, NumberInputStepper,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure, useToast,
} from "@chakra-ui/react";
import Decimal from 'decimal.js';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";

import {
  CheckListDataMap,
  ChecklistRecord,
  ConfirmorInfo,
  Currency,
  EquipmentCategoryType,
  EquipmentGroupType, EquipmentSupplierType,
  EquipmentType,
  Errors,
  PagePermissions,
  PartNo,
  ProductionCapacityBenchmark,
  ProductionLineType,
  ProjectContextType,
  ProjectWithRdsData,
  UniqueProcessType,
  ProjectAdditionalInfo,
  UpdateSummaryInputData,
  RationalizationSummaryInputData,
  GeneralPurposeChecklistData,
} from '../../contexts/types';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { formatNumber, formatYearMonthByLocale, getLocalCurrencyId, hasDecimal, isValidDateFormat, isValidDateRange, padYearMonth, sanitizeFileName, resolveDisplayPartNoByKey } from '../../utils/utils';
import { CustomNumberInput } from './CustomNumberInput';

import CheckList from "./CheckList";
import ProductionCapacityBenchmarkModal from "./ProductionCapacityBenchmarkModal";

import ReactFlow, {
  ConnectionLineType,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { v4 } from 'uuid';
import { Messages, PurposeType, ValidDateRangeEnd, ValidDateRangeStart, WorkflowId, EquipmentUsageType, isSimpleApplicationForm } from "../../contexts/constants";
import { useAppSelector } from "../../store/hooks";
import { convertEquipmentData } from "../../utils/calculation";
import GoogleAPI from "../../utils/GoogleAPI";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import '../common.css';
import ConfirmorList from "./ConfirmorList";
import CurrencyList from "./CurrencyList";
import HelpIcon from "./HelpIcon";
import { drawProcessFlow, fetchProcess, nodeTypes } from "./ProcessFlow";

// コンテキスト
import dayjs from "dayjs";
import EstimateCheckList from "../Modal/EstimateCheckList";
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";
import UpdateSummaryModal from "../Modal/UpdateSummaryModal";
import RationalizationSummaryModal from "../Modal/RationalizationSummaryModal";
import GeneralPurposeChecklistModal from "../Modal/GeneralPurposeChecklistModal";
import EquipmentListModal from "../Modal/EquipmentListModal"
import EquipmentEditModal from "../Modal/EquipmentEditModal"
import FileUploadModal from "../Modal/FileUploadModal";
import EquipmentGroupSelectModal from "../Modal/EquipmentGroupSelectModal";
import EquipmentSupplierSelectModal from "../Modal/EquipmentSupplierSelectModal";
import { REGEX_BRANCH, isModifiedRow, getBaseId, getBaseTotalQuantity, calculateTotalQuantity, isDisabledAddBranch, renumberDisplayOrder } from "../../utils/equipmentUtils";
import { getBilingualDisplayName, getMultilingualDisplayName } from "../../utils/i18nDisplay";

const cellStyle: React.CSSProperties = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
}

/**
 * 設備投資パラメータ
 */
interface EquipmentProps {
  /** プロジェクトID */
  projectId: string;
  /** 設備用途区分 */
  equipmentUsageType: string;
}

/**
 * Confirmor型を定義
 */
interface Confirmor {
  equipment?: {
    companyDivision?: string[];
    procurementDivision?: string[];
  };
}

/**
 * 新規投資申請画面(設備投資)
 * @returns
 */
const Equipment: React.FC<EquipmentProps> = ({ projectId, equipmentUsageType }) => {
  const { t } = useTranslation();
  // ------ 共通 ------------------------------------------------------------------------------------
  /** 設備件数（初期値） */
  const DEFAULT_EQUIPMENT_COUNT = 5;

  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.Equipment.title');

  /** スプレッドシート格納先 */
  const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/Equipment";

  /** 最大単価 */
  const MAX_UNIT_PRICE = 999999999999;

  /** 単価の小数点以下桁数 */
  const UNIT_PRICE_DECIMAL_PLACES = 2;

  /** 最大数量 */
  const MAX_QUANTITY = 99999;

  /** 最大人員数 */
  const MAX_NUMBER_OF_PEOPLE_REQUIRED = 999;

  /** 最大償却年数 */
  const MAX_DEPRECIATION_PERIOD = 99;

  /** Google API汎用処理クラス */
  const googleAPI: GoogleAPI = new GoogleAPI();

  /** 入力項目名幅 */
  const labelWidth = 160;


  /** 正規表現 - 変更行 */
  const regexBranchRow = /^[\s\S]{23}-\d{3}$/;

  // 精度を50桁に設定
  Decimal.set({ precision: 50 });

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);

  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  /** ユニークプロセス情報 */
  const [uniqueProcessData, setUniqueProcessData] = useState<UniqueProcessType[]>([]);

  /** プロジェクト情報 */
  const [projectWithRdsData, setProjectWithRdsData] = useState<ProjectWithRdsData | null>(null);

  /** 設備大分類情報 */
  const [equipmentCategories, setEquipmentCategories] = useState<EquipmentCategoryType[]>([]);

  /** 設備分類情報 */
  const [equipmentGroups, setEquipmentGroups] = useState<EquipmentGroupType[]>([]);

  /** 設備サプライヤー情報 */
  const [equipmentSuppliers, setEquipmentSuppliers] = useState<EquipmentSupplierType[]>([]);

  /** パーツNo */
  const [partNo, setPartNo] = useState<Pick<PartNo, "partNo" | "displayPartNo" | "quantityAndPrice">[]>([]);

  // 通貨マスタ情報
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);

  /** ローカル通貨名称 */
  const [localCurrencyName, setLocalCurrencyName] = useState<string>("");

  /** 選択インデックス */
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  /** 増産前生産数量過去ベスト（ライン全体） */
  const [bestProductionQuantity, setBestProductionQuantity] = useState<string>("0");

  /** 製造ライン情報 */
  const [productionLine, setProductionLine] = useState<ProductionLineType>({
    productionLineId: "",
    productionLineName: "",
    cycleTime: null,
    workingDays: null,
    nonWorkingHours: null,
    yieldRate: null,
    bestProductionQuantity: null,
    updateDate: "",
  });

  /** 製造ラインID（メモの再計算判定に使用するため変数化） */
  const currentProductionLineId = productionLine?.productionLineId;

  /**
   * 製造ライン情報の配列（子コンポーネントへの受け渡し用）
   * 毎レンダーで新しい配列を生成すると、受け取り側の useEffect が
   * （通信によるContext更新等に伴う）親の再レンダーごとに再発火し、
   * 入力中の値が初期値へ戻されてしまうため、参照を安定させる。
   * なお、ライン新規採番時に productionLine を直接書き換えている箇所があり
   * オブジェクト参照が変化しないケースがあるため、ID自体も依存に含める。
   */
  const productionLineDataList = useMemo(
    () => (currentProductionLineId ? [productionLine] : []),
    [productionLine, currentProductionLineId]
  );

  /** エラー情報 */
  const [equipmentErrors, setEquipmentErrors] = useState<Errors>({
    yieldRate: {
      isError: false,
      errorMessage: "",
    },
    bestProductionQuantity: {
      isError: false,
      errorMessage: "",
    },
  });
  /** アップロード可能な画像ファイルの拡張子 */
  const allowedImageExtensions = ["jpg", "jpeg", "png"];

  /** アップロード可能なファイルの拡張子 */
  const allowedExtensions = ["xlsx", "xls"];

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  /** どの部署の確認かを表す */
  const [confirmSource, setConfirmSource] = useState<string>("");

  /** プロセスフローのノード情報 */
  const [nodes, setNodes, onNodesChange] = useNodesState([]);

  /** プロセスフローのエッジ情報（線） */
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  /** モーダル表示制御用：確認モーダル */
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure()

  /** 設備入力用ヘッダスタイル */
  const inputModalHeaderStyle = {
    fontSize: "16px",
  };

  /** 設備入力用テキストスタイル */
  const inputModalTextStyle = {
    paddingLeft: "17px",
    paddingRight: "17px",
    fontSize: "16px",
    width: "100%",
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  /** 設備入力用ボタンテキストスタイル */
  const inputModalButtonTextStyle = {
    padding: "0px",
    fontSize: "16px",
    width: "160px",
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    // textAlign: "left",
    fontWeight: 'normal',
  };

  /** モーダル表示制御用：設備入力用のモーダル */
  const { isOpen: isOpenInputModal, onOpen: onInputModalOpen, onClose: onCloseInputModal } = useDisclosure();

  /** GoogleSpreadボタン制御 */
  const [isLoading, setIsLoading] = useState(false);

  /** 設備入力用のモーダルの画面入力件数 */
  const [equipmentCount, setEquipmentCount] = useState<number>(DEFAULT_EQUIPMENT_COUNT);

  /** 押下ノード */
  const [clickedNode, setClickedNode] = useState<UniqueProcessType | null>(null);

  /** 入力モード */
  const [inputMode, setInputMode] = useState<"google" | "manual">("manual");

  /** スプレッドシートウィンドウ管理（key：スプレッドシートID） */
  const [spreadSheetWindowss, setSpreadSheetWindowss] = useState<Window | null>(null);

  /** 設備情報 */
  const [equipmentData, setEquipmentData] = useState<EquipmentType[]>([]);

  /** 入力エラー情報 */
  const defaultErrors: Errors = {

    /** 設備名 */
    equipmentName: {
      isError: false,
      errorMessage: ""
    },

    /** 単価 */
    unitPrice: {
      isError: false,
      errorMessage: ""
    },

    /** 数量 */
    quantity: {
      isError: false,
      errorMessage: ""
    },

    quantityUnit: {
       isError: false, 
       errorMessage: "" 
    },

    /** 通貨 */
    currencyId: {
      isError: false,
      errorMessage: ""
    },

    /** 設備に必要な人員数 */
    numberOfPeopleRequiredForEquipment: {
      isError: false,
      errorMessage: ""
    },

    /** 設備なし時に必要な人員数 */
    numberOfPeopleRequiredForWithoutEquipment: {
      isError: false,
      errorMessage: ""
    },

    // RDSデータ
    /** 設備サプライヤーID */
    equipmentSupplierId: {
      isError: false,
      errorMessage: ""
    },

    /** 設備グループID */
    equipmentGroupId: {
      isError: false,
      errorMessage: ""
    },

    /** 償却年数 */
    depreciationPeriod: {
      isError: false,
      errorMessage: ""
    },

    /** 償却開始月 */
    depreciationStartMonth: {
      isError: false,
      errorMessage: ""
    },

    /** 検収月 */
    acceptanceMonth: {
      isError: false,
      errorMessage: ""
    },

    /** 申請予定月 */
    applicationPlannedMonth: {
      isError: false,
      errorMessage: ""
    },
  };

  /** エラー情報 */
  const [errorsList, setErrorsList] = useState<Errors[]>([]);

  /** モーダル表示制御用：設備サプライヤー選択 */
  const { isOpen: isOpenSupplierModal, onOpen: onOpenSupplierModal, onClose: onCloseSupplierModal } = useDisclosure();

  /** モーダル表示制御用：設備分類選択 */
  const { isOpen: isOpenGroupModal, onOpen: onOpenGroupModal, onClose: onCloseGroupModal } = useDisclosure()

  /** 【チェックリスト入力】モーダル表示制御 */
  const { isOpen: isOpenProductListModal, onOpen: onOpenProductListModal, onClose: onCloseProductListModal } = useDisclosure();

  /** 【ファイルアップロード】モーダル表示制御 */
  const { isOpen: isOpenFileUploadModal, onOpen: onOpenFileUploadModal, onClose: onCloseFileUploadModal } = useDisclosure();

  /** 【画像アップロード】モーダル表示制御 */
  const { isOpen: isOpenImageUploadModal, onOpen: onOpenImageUploadModal, onClose: onCloseImageUploadModal } = useDisclosure();

  /** 【設備一覧】モーダル表示制御 */
  const { isOpen: isOpenEquipmentListModal, onOpen: onOpenEquipmentListModal, onClose: onCloseEquipmentListModal } = useDisclosure();

  /** 【設備編集】モーダル表示制御 */
  const { isOpen: isOpenEquipmentEditModal, onOpen: onOpenEquipmentEditModal, onClose: onCloseEquipmentEditModal } = useDisclosure();

  /** 【設備一覧】チェックリスト更新状態 */
  const [checkListUpdateStatus, setCheckListUpdateStatus] = useState(false);

  /** 【設備一覧】M_CEAS_EQUIPMENT */
  const [equipmentListData, setEquipmentListData] = useState<EquipmentType[]>([]);

  /** 【設備編集】全設備データ（過去PJ設備含む） */
  const [equipmentEditData, setEquipmentEditData] = useState<EquipmentType[]>([]);

  /** このセッション内で仮登録した装置・機械のID（即座にドロップダウンに反映するため） */
  const [registeredCandidateIds, setRegisteredCandidateIds] = useState<Set<string>>(new Set());

  /** モーダル表示用: 正式マスタ + このプロジェクトで使用中の仮登録データ + セッション内仮登録 */
  const availableEquipmentGroups = useMemo(() => {
    const usedGroupIds = new Set(
      [...equipmentData, ...equipmentListData]
        .filter(e => e.equipmentGroupId)
        .map(e => e.equipmentGroupId)
    );
    return equipmentGroups.filter(g => {
      if (!g.isCandidate) return true;
      if (g.equipmentGroupId && registeredCandidateIds.has(g.equipmentGroupId)) return true;
      return g.equipmentGroupId ? usedGroupIds.has(g.equipmentGroupId) : false;
    });
  }, [equipmentGroups, equipmentData, equipmentListData, registeredCandidateIds]);

  /** 【過去ベストベンチマーク】モーダル表示制御 */
  const { isOpen: isOpenBenchmarkModal, onOpen: onOpenBenchmarkModal, onClose: onCloseBenchmarkModal } = useDisclosure();

  /** 【過去ベストベンチマーク】データ */
  const [benchmarkData, setBenchmarkData] = useState<ProductionCapacityBenchmark[]>([]);

  /** 【更新前後サマリー】 モーダル表示制御 */
  const { isOpen: isOpenUpdateSummaryModal, onOpen: onOpenUpdateSummaryModal, onClose: onCloseUpdateSummaryModal } = useDisclosure();

  /** 【更新前後サマリー】データ */
  const [updateSummaryData, setUpdateSummaryData] = useState<UpdateSummaryInputData[]>([]);

  /** 【合理化前後サマリー】 モーダル表示制御 */
  const { isOpen: isOpenRationalizationSummaryModal, onOpen: onOpenRationalizationSummaryModal, onClose: onCloseRationalizationSummaryModal } = useDisclosure();

  /** 【合理化前後サマリー】データ */
  const [rationalizationSummaryData, setRationalizationSummaryData] = useState<RationalizationSummaryInputData[]>([]);

  /** 【汎用設備チェックリスト】 モーダル表示制御 */
  const { isOpen: isOpenGeneralPurposeChecklistModal, onOpen: onOpenGeneralPurposeChecklistModal, onClose: onCloseGeneralPurposeChecklistModal } = useDisclosure();

  /** 【汎用設備チェックリスト】データ */
  const [generalPurposeChecklistData, setGeneralPurposeChecklistData] = useState<GeneralPurposeChecklistData | null>(null);

  /** 【設備一覧】r_ceas_check_list（本文は持たず、有無フラグ＋更新日時のみ保持） */
  const [estimateCheckListData, setEstimateCheckListData] = useState<{
    [equipmentId: string]: { hasEstimateCheckList: boolean; updateDate: string | null };
  }>({});

  /** １０カ条チェックリスト情報 */
  const [checkListData, setCheckListData] = useState<CheckListDataMap>({});

  /** １０か条チェックリストマスタ（m_ceas_checklist） */
  const [checklistMasterList, setChecklistMasterList] = useState<ChecklistRecord[]>([]);

  /** 最大設備数 */
  const MAX_EQUIPMENT_COUNT = 200;

  /** 設備投資情報（プロジェクト）※★アイコン表示や設備投資合計金額算出にて参照 */
  const [equipmentByProjectData, setEquipmentByProjectData] = useState<EquipmentType[]>([]);

  /** 設備投資合計金額 */
  const [equipmentSumAmount, setEquipmentSumAmount] = useState<Decimal>();

  /** 設備に必要な合計人員数 */
  const [sumNumberOfPeopleRequiredForEquipment, setSumNumberOfPeopleRequiredForEquipment] = useState<Decimal>();

  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);

  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});

  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    procurementDivision: [],
  });

  /** useRef */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // 保存状態
  const [isSaving, setIsSaving] = useState(false);

  /** *********************************************************************************************************
   * 設備投資画面 関連
   ********************************************************************************************************* */
  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.Equipment, projectWithRdsData?.purpose, employeeId, divisionId, projectWithRdsData?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, projectWithRdsData, divisionId]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId]);

  useEffect(() => {
    if (confirmor?.equipment) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.equipment?.[division as keyof Confirmor['equipment']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  /**
   * 初期表示
   */
  useEffect(() => {
    /**
     * GAPIロードとサインインを行う。
     */
    const initGapi = async () => {
      try {
        // Google API 初期化
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }

        // Googleアカウント認証
        if (! await googleAPI.signIn()) {
          throw new Error("signIn error");
        }
      } catch (error) {
        console.error("Google API 初期化エラー:", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.googleApiInitFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    if (projectId) {
      // GAPIロードとサインイン
      initGapi();
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  }, [toast]);

  /**
   * 初期表示２
   */
  useEffect(() => {

    if (projectId) {
      fetchData();
    }
  }, [projectId, toast]);

  /**
   * DBデータ取得
   */
  const fetchData = async () => {
    try {
      const payload = {
        query: `
          query fetchEquipmentData($projectId: String, $useRDSData: Boolean, $hasDeleted: Boolean, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              revisedOpportunityNameByRevision
              opportunityName
              purpose
              applicationFormType
              projectStartDate
              projectEndDate
              currencyId
              companyPlantSnapshot
              productionLineId
              registrantDivisionId
              updateDate
              companyPlantId
              mmProductNameFreeWrite
              customerProductName
              customerName
              customerIndustryHierarchy
              applicationCategory
              carModel
            }
            getEquipmentCategory(hasDeleted: $hasDeleted) {
              equipmentCategoryId
              equipmentCategoryName { ja en }
              description
              codePrefix
              sortOrder
              isCandidate
            }
            getEquipmentGroup(hasDeleted: $hasDeleted) {
              equipmentGroupId
              equipmentGroupName { ja en }
              equipmentCategoryId
              sortOrder
              isCandidate
            }
            getEquipmentSupplier(hasDeleted: $hasDeleted) {
              equipmentSupplierId
              equipmentSupplierName { ja en }
              deleted
              isCandidate
              registeredBy
            }
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              quantityAndPrice
            }
            getCurrencyById(projectId: $projectId) {
              currencyId
              currencyName
              exchangeRate
            }
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              benchmarkData
              updateSummaryData
              rationalizationSummaryData
              generalPurposeChecklistData
              updateDate
            }
            getCheckList {
              nodeId
              parentNodeId
              nodeClass
              checklistNo
              contentNameJa
              contentNameEn
              conflictingConsiderationsJa
              conflictingConsiderationsEn
              order
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          hasDeleted: false,
          ...(reviewDate && { reviewDate })
        }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      // プロジェクト情報取得（案件名）
      if (response && response.data && response.data.getProjectById && response.data.getProjectById.opportunityName) {
        // プロジェクト情報設定
        setProjectWithRdsData(response.data.getProjectById);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.projectInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProjectById. errors = `, response?.errors);
      }

      // 設備大分類情報取得
      if (response && response.data && response.data.getEquipmentCategory) {
        setEquipmentCategories(response.data.getEquipmentCategory);
      } else {
        console.error(`Failed to call API getEquipmentCategory. errors = `, response?.errors);
      }

      // 設備分類情報取得
      if (response && response.data && response.data.getEquipmentGroup) {
        response.data.getEquipmentGroup.sort((a: EquipmentGroupType, b: EquipmentGroupType) =>
          a.equipmentGroupId?.localeCompare(b?.equipmentGroupId || "")
        );
        // 設備分類情報設定
        setEquipmentGroups(response.data.getEquipmentGroup);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentGroupInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getEquipmentGroup. errors = `, response?.errors);
      }

      // 設備サプライヤー情報取得
      if (response && response.data && response.data.getEquipmentSupplier) {
        response.data.getEquipmentSupplier.sort((a: EquipmentSupplierType, b: EquipmentSupplierType) =>
          a.equipmentSupplierId?.localeCompare(b?.equipmentSupplierId || "")
        )
        // 設備サプライヤー情報設定
        setEquipmentSuppliers(response.data.getEquipmentSupplier);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentSupplierInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getEquipmentSupplier. errors = `, response?.errors);
      }

      // プロジェクトに紐づくパーツNo取得
      if (response && response.data && response.data.getPartNo) {
        // パーツNo設定
        setPartNo(response.data.getPartNo);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.partNoInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getPartNo. errors = `, response?.errors);
      }

      // 通貨情報取得
      if (response && response.data && response.data.getCurrencyById) {
        // 通貨情報設定
        setCurrencyData(response.data.getCurrencyById);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.currencyInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getCurrencyById. errors = `, response?.errors);
      }

      // プロジェクト追加情報取得
      if (response?.data?.getProjectAdditionalInfoById) {
        const projectAdditionalInfo: ProjectAdditionalInfo = response?.data?.getProjectAdditionalInfoById;

        if (projectAdditionalInfo.benchmarkData) {
          // JSONパース
          const benchmarkData = JSON.parse(projectAdditionalInfo.benchmarkData);

          setBenchmarkData(benchmarkData);
        } else {
          setBenchmarkData([]);
        }

        // 更新前後サマリーデータ読み込み
        if (projectAdditionalInfo.updateSummaryData) {
          // JSONパース
          const updateSummaryData = JSON.parse(projectAdditionalInfo.updateSummaryData);
          setUpdateSummaryData(updateSummaryData);
        } else {
          setUpdateSummaryData([]);
        }

        // 合理化前後サマリーデータ読み込み
        if (projectAdditionalInfo.rationalizationSummaryData) {
          // JSONパース
          const rationalizationSummaryData = JSON.parse(projectAdditionalInfo.rationalizationSummaryData);
          setRationalizationSummaryData(rationalizationSummaryData);
        } else {
          setRationalizationSummaryData([]);
        }

        // 汎用設備チェックリストデータ読み込み
        if (projectAdditionalInfo.generalPurposeChecklistData) {
          try {
            // JSONパース
            const generalPurposeChecklistData = JSON.parse(projectAdditionalInfo.generalPurposeChecklistData);
            setGeneralPurposeChecklistData(generalPurposeChecklistData);
          } catch (e) {
            console.error(`Failed to parse generalPurposeChecklistData`, e);
            setGeneralPurposeChecklistData(null);
          }
        } else {
          setGeneralPurposeChecklistData(null);
        }
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.projectAdditionalInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProjectAdditionalInfoById. errors = `, response);
      }

      // チェックリストマスタ取得
      if (response?.data?.getCheckList) {
        const sorted = [...response.data.getCheckList].sort((a: ChecklistRecord, b: ChecklistRecord) => {
          const numA = parseInt(a.checklistNo, 10);
          const numB = parseInt(b.checklistNo, 10);
          if (numA !== numB) return numA - numB;
          return a.order - b.order;
        });
        setChecklistMasterList(sorted);
      } else {
        console.error(`Failed to call API getCheckList. errors = `, response?.errors);
      }

      // 製造ライン情報取得
      const productLineInfo = await fetchProductLine(response?.data?.getProjectById?.productionLineId);

      // 増産前生産数量過去ベスト設定
      if (productLineInfo?.bestProductionQuantity !== null && productLineInfo?.bestProductionQuantity !== undefined) {
        setBestProductionQuantity(String(productLineInfo?.bestProductionQuantity));
      }

      // プロセスフロー描画（setPartNoの反映を待たずstateを参照してしまうため、取得直後の値を明示的に渡す）
      drawProcessFlowWithFetch(response?.data?.getPartNo);

    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.dataFetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * 製造ライン情報取得
   * @param productionLineId 製造ラインID
   */
  const fetchProductLine = async (productionLineId: string): Promise<ProductionLineType | undefined> => {
    try {
      if (!productionLineId) {
        return;
      }

      const payload = {
        query: `
          query getProductionLineById($projectId: String, $productionLineId: String, $reviewDate: String) {
            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) {
              productionLineId
              productionLineName
              cycleTime
              workingDays
              nonWorkingHours
              yieldRate
              bestProductionQuantity
              updateDate
            }
          }`,
        variables: { projectId, productionLineId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      // 製造ライン情報取得
      if (response?.data?.getProductionLineById?.productionLineId) {
        // 製造ライン情報取得設定
        setProductionLine(response.data.getProductionLineById);
        return response.data.getProductionLineById;
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.productionLineInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProductionLineById. errors = `, response?.errors);
      }
    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.dataFetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }

  /**
   * 指定したプロパティ名の値を更新する。
   * @param propertyName プロパティ名
   * @param newValue 新しい値
   */
  const changeProductionLine = (propertyName: string, newValue: any) => {
    setProductionLine((prevState) => ({
      ...prevState,
      [propertyName]: newValue,
    }));
  }

  /**
   * プロセスフロー描画
   * @param partNoOverride 呼び出し直後で`partNo`ステートがまだ反映されていない場合に使う、取得済みのPartNo一覧（未指定時はstateの`partNo`を使用）
   */
  const drawProcessFlowWithFetch = async (partNoOverride?: Pick<PartNo, "partNo" | "displayPartNo">[]) => {
    // ユニークプロセス情報取得
    const uniqueProcessData = await fetchProcess(projectId, TITLE, WebAPI, toast, reviewDate);

    // 設備情報取得
    const equipmentData = await fetchEquipmentByProjectId(projectId);
    setEquipmentByProjectData(equipmentData || []);

    // プロジェクトに紐づくプロセス情報取得
    const { nodes, edges } = drawProcessFlow(uniqueProcessData, handleNodeClick, equipmentData, partNoOverride ?? partNo);

    setUniqueProcessData(uniqueProcessData ?? []);
    setNodes(nodes ?? []);
    setEdges(edges ?? []);
  }

  /**
   * 金額を算出する。
   * @param unitPrice 単価
   * @param quantity 数量（増減量）
   * @returns 金額（単価 * 数量）。数量がマイナスの場合は0を返す。
   */
  const calcAmount = (unitPrice: number | undefined, quantity: number | undefined, equipment: EquipmentType) => {
    if (!unitPrice || isNaN(unitPrice)) return undefined;
    if (quantity === undefined || quantity === null || isNaN(quantity)) return undefined;

    // 既存設備の減少（マイナス数量）の場合、投資金額は0
    if (quantity <= 0) {
      return new Decimal(0);
    }

    let unitPriceDecimal = new Decimal(unitPrice);
    let quantityDecimal = new Decimal(quantity);

    const amount = unitPriceDecimal.times(quantityDecimal);

    return amount;
  }

  /**
   * 同一equipmentId・同一displayOrderで重複する設備の枝番振り替えとソート
   * 自プロジェクト（isReadonly === false）の設備に枝番を付与し、displayOrderを振り替える
   * @param equipments 設備情報配列（破壊的に変更）
   */
  const resolveDuplicateEquipments = (equipments: EquipmentType[]) => {
    // 同一equipmentId・同一displayOrderでグループ化
    const duplicateGroups = new Map<string, EquipmentType[]>();
    equipments.forEach(e => {
      if (!e.equipmentId) return;
      const key = `${e.equipmentId}_${e.displayOrder}`;
      if (!duplicateGroups.has(key)) {
        duplicateGroups.set(key, []);
      }
      duplicateGroups.get(key)!.push(e);
    });

    duplicateGroups.forEach((group) => {
      // 他プロジェクトと自プロジェクトの両方が存在する重複のみ処理
      const hasOtherProject = group.some(e => e.isReadonly);
      const hasSelfProject = group.some(e => !e.isReadonly);
      if (!hasOtherProject || !hasSelfProject || group.length < 2) return;

      // 自プロジェクトの設備に枝番を付与
      group.filter(e => !e.isReadonly).forEach(e => {
        const baseId = getBaseId(e.equipmentId!);
        // 同一ベースIDの既存最大枝番を取得
        const maxBranchNo = equipments
          .filter(d => d.equipmentId && d.equipmentId.startsWith(baseId) && d.equipmentId !== baseId)
          .reduce((max, d) => {
            const match = d.equipmentId!.match(/-([0-9]{3})$/);
            return match ? Math.max(max, parseInt(match[1], 10)) : max;
          }, 0);
        const newBranchNo = maxBranchNo + 1;
        e.equipmentId = `${baseId}-${newBranchNo.toString().padStart(3, "0")}`;
        e.displayOrder = Math.floor(e.displayOrder || 0) + newBranchNo * 0.001;
      });
    });

    // displayOrder昇順でソート
    equipments.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  };

  /**
   * 空の設備情報を追加する
   * @param equipmentData 設備情報
   */
  const addEmptyEquipment = (equipmentData: EquipmentType[], count: number) => {
    const startOrder = equipmentData.reduce((max, e) => Math.max(max, Math.floor(e.displayOrder || 0)), 0);
    let order = startOrder;
    while (equipmentData.length < count) {
      equipmentData.push({ displayOrder: ++order });
    }
  }

  /**
   * ノード押下時
   * @param node 対象ノード情報
   */
  const handleNodeClick = async (process: UniqueProcessType) => {
    try {
      console.log(`processId = ${process.uniqueProcessId}`);

      // (1) ProcessId で CapexRdsEquipment を取得
      const payload = {
        query: `
          query getEquipmentByProcessId($projectId: String, $processId: String, $reviewDate: String) {
            getEquipmentByProcessId(projectId: $projectId, processId: $processId, reviewDate: $reviewDate) {
              projectId
              equipmentId
              equipmentName
              fixedAssetCode
              scheduledOrderDate
              actualOrderDate
              installationDate
              unitPrice
              currencyId
              quantity
              totalQuantity
              quantityUnit
              amount
              numberOfPeopleRequiredForEquipment
              unskilledWorker
              multiSkilledWorker
              technician
              numberOfPeopleRequiredForWithoutEquipment
              equipmentGroupId
              equipmentSupplierId
              updateDate
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
              applicationPlannedMonth
              displayOrder
            }
          }`,
        variables: {
          projectId: projectId,
          processId: process.uniqueProcessId,
          ...(reviewDate && { reviewDate })
        }
      };

      setErrorsList([]);
      setEquipmentCount(DEFAULT_EQUIPMENT_COUNT);

      // DB情報取得
      const response = await WebAPI.request(payload);

      if (response && response.data && response.data.getEquipmentByProcessId) {
        // 設備情報取得
        const equipmentData: EquipmentType[] = response.data.getEquipmentByProcessId;


        equipmentData.map(e => {
          e.isReadonly = e.projectId !== projectId;
        });

        // 重複設備の枝番振り替えとソート
        resolveDuplicateEquipments(equipmentData);

        if (equipmentCount === 0 && equipmentData.length === 0) {
          // 空データ追加
          addEmptyEquipment(equipmentData, DEFAULT_EQUIPMENT_COUNT);
        } else if (equipmentData.length < equipmentCount) {
          // 空データ追加
          addEmptyEquipment(equipmentData, equipmentCount);
        } else {
          // 設備数更新
          setEquipmentCount(equipmentData.length);
        }

        // 設備情報設定
        setEquipmentData(equipmentData);
        const unitinfo: EquipmentUnitSetting[] = equipmentData
          .filter((item) => item.projectId)
          .map((item) => ({
            displayOrder: item.displayOrder ?? 0,
            quantityUnit: item.quantityUnit,
          }));
        setUnitSnap(unitinfo);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getEquipmentByProcessId. errors = `, response?.errors);
        return;
      }
    } catch (error) {
      console.error("工程クリック時のデータ取得エラー:", error);
    }

    // モーダルを開くための処理
    setClickedNode(process);

    // モーダル表示
    onInputModalOpen();
  };

  /**
   * チェックリスト入力ボタン押下
   */
  const handleProductListButtonClick = async () => {

    // 設備情報一覧取得
    getEquipmentInfoList();

    // モーダル表示
    onOpenProductListModal();

  };

  /**
   * 設備一覧ボタン押下
   */
  const handleEquipmentListButtonClick = async () => {

    // 設備情報一覧取得
    getEquipmentInfoList();

    // モーダル表示
    onOpenEquipmentListModal();

  };

  /**
   * モーダル非表示
   */
  useEffect(() => {

    if (checkListUpdateStatus) {
      const fetchData = async () => {
        await getEquipmentInfoList();
      };
      fetchData();
      setCheckListUpdateStatus(false);
    }

  }, [checkListUpdateStatus]);

  /**
   * 設備情報一覧取得
   */
  const getEquipmentInfoList = async () => {

    const functionName = "handleProductListButtonClick";

    try {

      // 変数宣言
      const payload = {
        query: `
          query getEquipmentInfoList($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              equipmentName
              fixedAssetCode
              scheduledOrderDate
              actualOrderDate
              installationDate
              unitPrice
              currencyId
              quantity
              totalQuantity
              quantityUnit
              amount
              numberOfPeopleRequiredForEquipment
              unskilledWorker
              multiSkilledWorker
              technician
              numberOfPeopleRequiredForWithoutEquipment
              equipmentGroupId
              equipmentSupplierId
              updateDate
              processId
              equipmentSupplierId
              equipmentGroupId
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
              applicationPlannedMonth
            }
            getCheckListById(projectId: $projectId, reviewDate: $reviewDate) {
              items {
                equipmentId
                hasEstimateCheckList
                updateDate
                checkList
              }
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentByProjectId) {

        // 設備情報取得
        const equipmentListData: EquipmentType[] = response.data.getEquipmentByProjectId;


        // 通貨名設定
        equipmentListData.forEach((equipment) => {
          if (equipment.currencyId) {
            const currencyInfo = currencyData.find((currency) => currency.currencyId === equipment.currencyId);
            if (currencyInfo?.currencyName) {
              equipment.currencyName = currencyInfo.currencyName;
            }
          }
          // 設備分類名設定
          if (equipment.equipmentGroupId) {
            const groupInfo = equipmentGroups.find((group) => group.equipmentGroupId === equipment.equipmentGroupId);
            if (groupInfo?.equipmentGroupName) {
              equipment.equipmentGroupName = groupInfo.equipmentGroupName;
            }
          }
        });

        // 設備情報設定
        setEquipmentListData(equipmentListData);

      } else {

        // データ取得失敗
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getEquipmentByProjectId. errors = `, response?.errors);
        return;

      }

      if (response?.data?.getCheckListById) {

        const checkListByIdItems = response.data.getCheckListById.items ?? [];

        // 見積チェックリスト有無フラグ取得（本文は一覧表示時には取得しない）
        const estimateCheckListData = checkListByIdItems.reduce((acc: any, item: any) => {
          if (item.equipmentId) {
            acc[item.equipmentId] = {
              hasEstimateCheckList: !!item.hasEstimateCheckList,
              updateDate: item.updateDate,
            };
          }
          return acc;
        }, {});

        // 見積チェックリスト設定
        setEstimateCheckListData(estimateCheckListData);

        // １０カ条チェックリスト取得
        const checkListData = checkListByIdItems.reduce((acc: any, item: any) => {
          if (item.equipmentId) {
            acc[item.equipmentId] = {
              checkList: item.checkList,
              updateDate: item.updateDate,
            };
          }
          return acc;
        }, {});

        // １０カ条チェックリスト設定
        setCheckListData(checkListData);

      } else {

        // データ取得失敗
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.checklistInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getCheckListById. errors = `, response?.errors);
        return;

      }

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
    }

  };

  /**
   * 設備編集モーダル用データ取得（過去PJ設備含む）
   */
  const getEquipmentEditData = async () => {
    const functionName = "getEquipmentEditData";

    try {
      const payload = {
        query: `
          query getEquipmentForEdit($projectId: String, $reviewDate: String) {
            getEquipmentForEdit(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              equipmentName
              fixedAssetCode
              scheduledOrderDate
              actualOrderDate
              installationDate
              unitPrice
              currencyId
              quantity
              totalQuantity
              quantityUnit
              amount
              numberOfPeopleRequiredForEquipment
              unskilledWorker
              multiSkilledWorker
              technician
              numberOfPeopleRequiredForWithoutEquipment
              equipmentGroupId
              equipmentSupplierId
              updateDate
              processId
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
              applicationPlannedMonth
              displayOrder
              isReadonly
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentForEdit) {
        const editData: EquipmentType[] = response.data.getEquipmentForEdit;

        // 通貨名設定
        editData.forEach((equipment) => {
          if (equipment.currencyId) {
            const currencyInfo = currencyData.find((currency) => currency.currencyId === equipment.currencyId);
            if (currencyInfo?.currencyName) {
              equipment.currencyName = currencyInfo.currencyName;
            }
          }
        });

        // 設備分類名設定
        editData.forEach((equipment) => {
          if (equipment.equipmentGroupId) {
            const groupInfo = equipmentGroups.find((group) => group.equipmentGroupId === equipment.equipmentGroupId);
            if (groupInfo?.equipmentGroupName) {
              equipment.equipmentGroupName = groupInfo.equipmentGroupName;
            }
          }
        });

        // サプライヤー名設定
        editData.forEach((equipment) => {
          if (equipment.equipmentSupplierId) {
            const supplierInfo = equipmentSuppliers.find((supplier) => supplier.equipmentSupplierId === equipment.equipmentSupplierId);
            if (supplierInfo?.equipmentSupplierName) {
              equipment.equipmentSupplierName = supplierInfo.equipmentSupplierName;
            }
          }
        });

        // 重複設備の枝番振り替えとソート
        resolveDuplicateEquipments(editData);

        setEquipmentEditData(editData);
      } else {
        console.error(`${functionName}: Failed to get equipment data`);
        setEquipmentEditData([]);
        // データ取得失敗時はトースト表示してユーザにエラー通知する。
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.dataFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      setEquipmentEditData([]);
      // データ取得失敗時はトースト表示してユーザにエラー通知する。
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.dataFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * 「確認」ボタン押下処理
   */
  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    // フォーカスを外す
    event.currentTarget.blur();

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    if (!validateEquipment()) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.hasErrors'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 目的・設備区分に応じた必須チェックリスト入力チェック
    // 共通：10か条チェックリストがいずれかの設備で確認済みであることが必要
    const isTenPrinciplesChecklistConfirmed = Object.values(checkListData).some((entry) => {
      if (!entry?.checkList) {
        return false;
      }
      try {
        const parsed = typeof entry.checkList === 'string' ? JSON.parse(entry.checkList) : entry.checkList;
        return !!parsed?.tenPrinciplesChecked;
      } catch (e) {
        console.error(`Failed to parse checkList`, e);
        return false;
      }
    });

    if (!isTenPrinciplesChecklistConfirmed) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.tenPrinciplesChecklistRequired'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 増産の場合：過去ベストベンチマークが1件以上必要
    if (projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code && benchmarkData.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.benchmarkDataRequired'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 簡易入力モード（合理化・更新案件の簡易様式）では合理化前後サマリー・更新前後サマリーは必須にしない
    const isSimpleMode = isSimpleApplicationForm(projectWithRdsData?.purpose, projectWithRdsData?.applicationFormType);

    // 合理化の場合：合理化前後サマリーが1件以上必要（簡易入力モードでは任意）
    if (!isSimpleMode && projectWithRdsData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryData.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.rationalizationSummaryRequired'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 更新の場合：更新前後サマリーが1件以上必要（簡易入力モードでは任意）
    if (!isSimpleMode && projectWithRdsData?.purpose === PurposeType.UpdateAndOther.code && updateSummaryData.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.updateSummaryRequired'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 汎用設備の場合：汎用設備チェックリストが必要
    if (
      equipmentUsageType === EquipmentUsageType.GeneralPurpose.code &&
      (!generalPurposeChecklistData || !generalPurposeChecklistData.groupTotalCount?.trim() || !generalPurposeChecklistData.siteCount?.trim())
    ) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.generalPurposeChecklistRequired'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!projectWithRdsData || !projectWithRdsData.companyPlantId) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 全件登録必須チェック（過去ベストベンチマークを全設備分登録しないと通過できない仕様）は
    // 「1件以上必須チェック」のみ残す方針のためコメントアウト
    // if (benchmarkStatus === "tooMany") {
    //   toast({
    //     title: TITLE,
    //     description: t('features.InvestmentScreening.Equipment.validation.deletedBenchmarkData'),
    //     status: 'warning',
    //     duration: 5000,
    //     isClosable: true,
    //   });
    //   return;
    // }

    // if (benchmarkStatus !== "ok") {
    //   toast({
    //     title: TITLE,
    //     description: t('features.InvestmentScreening.Equipment.message.registerFromBenchmark'),
    //     status: 'warning',
    //     duration: 5000,
    //     isClosable: true,
    //   });
    //   return;
    // }

    // 歩留 / 最大サイクルタイム
    if (Number(productionLine?.yieldRate || 0) <= 0 || Number(productionLine?.cycleTime || 0) <= 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.validation.saveFromProcessConfirm'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // ローカル通貨の通貨コードを取得（会社/工場スナップショットから）
    const localCurrencyId = getLocalCurrencyId(projectWithRdsData?.companyPlantSnapshot);
    const localCurrencyName = currencyData.find(currency => currency.currencyId === localCurrencyId)?.currencyName;
    if (!localCurrencyName) {
      console.error(`通貨コード "${localCurrencyId}" の取得に失敗しました。`);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.localCurrencyMissing'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setLocalCurrencyName(localCurrencyName);

    let sumLocalCurrency = new Decimal(0);
    // 工場通貨へ金額変換
    const convEquipmentData = convertEquipmentData(equipmentByProjectData, localCurrencyName, currencyData);
    sumLocalCurrency = convEquipmentData.reduce((acc, item) => {
      if (!item.amount) {
        return acc; // 金額が存在しない場合はスキップ
      }
      return acc.plus(new Decimal(item.amount));
    }, new Decimal(0));

    // 設備投資合計金額
    setEquipmentSumAmount(sumLocalCurrency);

    // 設備に必要な合計人員数
    let sumNumberOfPeopleRequiredForEquipment = new Decimal(0);
    equipmentByProjectData.map((e: EquipmentType) => {
      sumNumberOfPeopleRequiredForEquipment = sumNumberOfPeopleRequiredForEquipment.plus(e.numberOfPeopleRequiredForEquipment || 0);
    });
    setSumNumberOfPeopleRequiredForEquipment(sumNumberOfPeopleRequiredForEquipment);

    // 確認モーダル表示
    onOpenConfirmModal();
  }

  /**
   * 設備投資情報を取得する。
   * @param projectId プロジェクトID
   * @returns 設備投資情報
   */
  const fetchEquipmentByProjectId = async (projectId: string): Promise<EquipmentType[] | null> => {
    try {
      const payload = {
        query: `
          query getEquipmentByProjectId($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              equipmentName
              unitPrice
              amount
              currencyId
              numberOfPeopleRequiredForEquipment
              processId
              unskilledWorker
              multiSkilledWorker
              technician
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      }

      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentByProjectId) {
        const equipmentData = response.data.getEquipmentByProjectId;
        return equipmentData;
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentInfo') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getEquipmentByProjectId. errors = `, response?.errors);
        return null;
      }
    } catch (error) {
      console.error("Exception getEquipmentByProjectId Error.", error);

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.fetchFailed', { name: t('features.InvestmentScreening.Equipment.message.equipmentInfo') }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  }

  /**
   * 確認モーダルで保存ボタン押下
   */
  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {

    // フォーカスを外す
    e.currentTarget.blur();

    // 保存状態を保存中に更新
    setIsSaving(true);

    try {
      // 製造ライン
      const updateProductLineResult = await updateProductLine();

      if (updateProductLineResult === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putProductionLine.`);
        return;
      } else if (!updateProductLineResult || updateProductLineResult !== "success") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.productionLineSaveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putProductionLine.`);
        return;
      }

      // 製造ライン情報取得
      await fetchProductLine(projectWithRdsData?.productionLineId!);

      // DBデータ取得
      await fetchData();

      // ワークフローテーブルを更新
      await updateConfirm(
        WebAPI,
        projectId,
        WorkflowId.Equipment,
        employeeId,
        confirmSource,
        "true",
        reviewDate,
      );
      // 確認担当者JSONを取得
      const confirmor = await getConfirmor(
        WebAPI,
        projectId,
        reviewDate,
      );
      setConfirmor(confirmor);
    } catch (error) {
      // TODO 処理失敗時は暫定でスルーする
    } finally {
      // 保存状態を未保存に更新
      setIsSaving(false);
    }

    toast({
      title: TITLE,
      description: t('features.InvestmentScreening.Equipment.message.saveSuccess'),
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    onCloseConfirmModal();
  }

  /**
   * バリデーションチェックを行う。
   */
  const validateEquipment = () => {
    let isError = false;

    const errors: Errors = {
      yieldRate: {
        isError: false,
        errorMessage: "",
      },
      bestProductionQuantity: {
        isError: false,
        errorMessage: "",
      },
    };

    const requiredMessage = t('features.InvestmentScreening.Equipment.validation.required');

    // 増産
    if (projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code) {

      // 増産前生産数量過去ベスト
      if (Number(productionLine?.bestProductionQuantity || 0) <= 0) {
        errors.bestProductionQuantity.isError = true;
        errors.bestProductionQuantity.errorMessage = t('features.InvestmentScreening.Equipment.validation.bestProductionQuantityRequired');
        isError = true;
      }

    }

    setEquipmentErrors(errors);

    return !isError;
  }

  /**
   * 製造ライン情報を保存する。
   */
  const updateProductLine = async (): Promise<string | null> => {
    try {

      if (!productionLine.productionLineId) {
        // フロント側で採番
        productionLine.productionLineId = `pl-${v4()}`;
      }

      // 登録パラメータ
      const input = {
        projectId: projectId,
        productionLineId: productionLine.productionLineId,
        productionLineName: productionLine.productionLineName,
        cycleTime: productionLine.cycleTime,
        workingDays: productionLine.workingDays,
        nonWorkingHours: productionLine.nonWorkingHours,
        yieldRate: productionLine.yieldRate,
        bestProductionQuantity: productionLine.bestProductionQuantity,
        updateDate: productionLine.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putProductionLine($input: productionLineInput!) {
              putProductionLine(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`putProductionLine result = `, result);
      return result?.data?.putProductionLine?.status;
    } catch (error) {
      console.error("Failed to call API putProductionLine. exception =", error);
      return null;
    }
  }

  /**
   * 設備投資情報を保存する。
   */
  const updateEquipment = async (): Promise<string | null> => {
    try {

      // 登録パラメータ
      const input = {
        projectId: projectId,
        updateDate: projectWithRdsData?.updateDate,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation updateEquipment($input: EquipmentInput!) {
              updateEquipment(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`updateEquipment result = `, result);
      return result?.data?.updateEquipment?.status;
    } catch (error) {
      console.error("Failed to call API updateEquipment. exception =", error);
      return null;
    }
  }

  /** *********************************************************************************************************
   * 設備入力用のモーダル 関連
   ********************************************************************************************************* */
  /**
   * ノード押下時 ⇒ 設備入力用のモーダル表示時
   */
  useEffect(() => {
    if (isOpenInputModal) {
      // 設備入力用のモーダル表示時
      equipmentData.forEach((equipment, idx) => {
        // 設備分類名セット
        if (equipment.equipmentGroupId) {
          const groupInfo = equipmentGroups.find(group => group.equipmentGroupId === equipment.equipmentGroupId);

          if (JSON.stringify(equipment.equipmentGroupName) !== JSON.stringify(groupInfo?.equipmentGroupName)) {
            changeEquipment(idx, "equipmentGroupName", groupInfo?.equipmentGroupName);
          }
        }

        // 設備サプライヤー名セット
        if (equipment.equipmentSupplierId) {
          const supplierInfo = equipmentSuppliers.find(supplier => supplier.equipmentSupplierId === equipment.equipmentSupplierId);

          if (JSON.stringify(equipment.equipmentSupplierName) !== JSON.stringify(supplierInfo?.equipmentSupplierName)) {
            changeEquipment(idx, "equipmentSupplierName", supplierInfo?.equipmentSupplierName);
          }
        }

        // 通貨名セット
        if (equipment.currencyId) {
          const currencyInfo = currencyData.find(currency => currency.currencyId === equipment.currencyId);

          if (equipment.currencyName !== currencyInfo?.currencyName) {
            changeEquipment(idx, "currencyName", currencyInfo?.currencyName);
          }
        }
      });
    }

    // equipmentGroups, equipmentSuppliersが設定された段階がポイント
  }, [equipmentGroups, equipmentSuppliers, equipmentData, isOpenInputModal]);

  /**
   * 設備投資合計金額の自動計算
   */
  useEffect(() => {
    if (!projectWithRdsData || !equipmentListData || equipmentListData.length === 0) {
      return;
    }

    const localCurrencyId = getLocalCurrencyId(projectWithRdsData.companyPlantSnapshot);
    const localCurrencyName = currencyData.find(
      currency => currency.currencyId === localCurrencyId
    )?.currencyName;

    if (!localCurrencyName) {
      return;
    }

    const convEquipmentData = convertEquipmentData(equipmentListData, localCurrencyName, currencyData);
    const sumLocalCurrency = convEquipmentData.reduce((acc, item) => {
      if (!item.amount) {
        return acc;
      }
      return acc.plus(new Decimal(item.amount));
    }, new Decimal(0));

    setEquipmentSumAmount(sumLocalCurrency);
  }, [equipmentListData, currencyData, projectWithRdsData]);

  /**
   * 指定した箇所の設備情報を変更する。
   * @param index インデックス
   * @param propertyName プロパティ名
   * @param newValue 値
   */
  const changeEquipment = (index: number, propertyName: string, newValue: any) => {
    setEquipmentData(prevState =>
      prevState.map((item, idx) => {
        if (idx !== index) return item;

        // invalidFieldsから該当フィールドを削除
        const updatedInvalidFields = { ...item.invalidFields };
        delete updatedInvalidFields[propertyName];

        return {
          ...item,
          [propertyName]: newValue,
          invalidFields: Object.keys(updatedInvalidFields).length > 0 ? updatedInvalidFields : undefined,
        };
      })
    );
  }

  /**
   * 設備情報を変更する。
   * @param event 変更イベント情報
   */
  const handleChangeEquipment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const index = Number(event.target.dataset.index);

    changeEquipment(index, name, value);
  }

  /**
   * 「設備数」変更時
   * @param valueString 設定数（型：文字列）
   */
  const handleEquipmentCount = (valueString: string) => {
    const count = Number(valueString);

    if (count > MAX_EQUIPMENT_COUNT) {
      return;
    }

    if (equipmentData.length < count) {

      //  空データ追加
      addEmptyEquipment(equipmentData, count);
      setEquipmentData(equipmentData);
    }

    // 「設備数」設定
    setEquipmentCount(count);
  }

  /**
   * 「画面で入力」ボタン押下
   */
  const handleMaualClick = () => {
    spreadSheetWindowss?.close();

    // スプレッドシートで空欄（""）だった行に「台（unit）」をセットし、赤枠・エラーを解除
    setEquipmentData(prevState => {
      if (!Array.isArray(prevState)) return prevState;
      
      return prevState.map(row => {
        if (row.quantityUnit === "") {
          const updatedInvalidFields = { ...row.invalidFields };
          delete updatedInvalidFields["quantityUnit"];
          return {
            ...row,
            quantityUnit: "unit",
            invalidFields: Object.keys(updatedInvalidFields).length > 0 ? updatedInvalidFields : undefined,
          };
        }
        return row;
      });
    });

    // バリデーションエラーメッセージを解除
    setErrorsList(prevState => {
      // 過去の仕様で配列以外（単一オブジェクト）が入っている場合は、そもそもエラーメッセージは表示されていない状態のためそのまま返す
      if (!Array.isArray(prevState)) {
        return prevState;
      }
      
      // 配列の場合はエラーフラグをリセットする
      return prevState.map(errors => ({
        ...errors,
        quantityUnit: { isError: false, errorMessage: "" },
      }));
    });
  };

  // 固定選択肢のコードと多言語キーの対応
  const QUANTITY_UNIT_KEYS = [
    { code: "unit", key: "features.InvestmentScreening.Equipment.options.quantityUnit.unit" },
    { code: "set",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.set" },
    { code: "pcs",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.pcs" },
  ] as const;

  /**
   * 【出力用】DB保存値（unit/set/pcs または自由入力）をスプレッドシート表示名に変換
   */
  const toSpreadsheetUnitLabel = (code?: string | null): string => {
    const targetCode = code || "unit"; // 初期値
    const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
    // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
    return matched ? t(matched.key) : targetCode;
  };

  /**
   * 【取込用】スプレッドシートのセル値（台/式/個 または自由入力）をDB保存用コードに変換
   */
  const toDbQuantityUnitCode = (cellValue?: string | null): string => {
    const val = (cellValue || "").trim();
    const matched = QUANTITY_UNIT_KEYS.find(item => t(item.key) === val || item.code.toLowerCase() === val.toLowerCase());
    // プルダウン表示名に一致すればコード（例: "unit"）、なければ自由入力値をそのまま返す
    return matched ? matched.code : (val || "unit");
  };

  /**
   * 「GoogleSpread」ボタン押下
   */
  const handleGoogleSpreadClick = async () => {
    if (!clickedNode) {
      console.error("クリックされたノードが存在しません");
      return;
    }

    if (inputMode === "google") {
      return;
    }

    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");

      // Googleアカウント認証（念のため）
      if (! await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // スプレッドシート親フォルダID取得
      // マイドライブに作成（権限制御のため共有ドライブは使用しない）
      const parentId = await googleAPI.createFolderPath(SPREAD_SHEET_PATH, null);

      // 対象ファイル名でスプレッドシートを生成する（既に存在する場合は作成しない）
      // ファイル名に使用できない文字（スラッシュ等）を全角に置換
      const fileName = sanitizeFileName(`${clickedNode.uniqueProcessName}(${clickedNode.uniqueProcessId})`);
      const activeSpreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId!, null);
      clickedNode.spreadsheetId = activeSpreadsheetId;

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(activeSpreadsheetId);
        if (activeSpreadsheetId) {
          // スプレッドシートウィンドウを保持
          setSpreadSheetWindowss(newWindow);
        }

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            setInputMode("manual");
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.error("GoogleSpreadシートがブロックされました");
      }

      const HEADER = [
        t('features.InvestmentScreening.Equipment.field.equipmentName'),
        t('features.InvestmentScreening.Equipment.field.unitPrice'),
        t('features.InvestmentScreening.Equipment.field.currency'),
        t('features.InvestmentScreening.Equipment.field.quantity'),
        t('features.InvestmentScreening.Equipment.field.quantityUnit'),
        t('features.InvestmentScreening.Equipment.field.depreciationYears'),
        `${t('features.InvestmentScreening.Equipment.field.applicationMonth')}\n(YYYY-MM)`,
        `${t('features.InvestmentScreening.Equipment.field.inspectionMonth')}\n(YYYY-MM)`,
        `${t('features.InvestmentScreening.Equipment.field.depreciationStartMonth')}\n(YYYY-MM)`,
        `${t('features.InvestmentScreening.Equipment.field.personnelPerShift')}\n${t('features.InvestmentScreening.Equipment.field.unskilledWorker')}`,
        `${t('features.InvestmentScreening.Equipment.field.personnelPerShift')}\n${t('features.InvestmentScreening.Equipment.field.multiSkilledWorker')}`,
        `${t('features.InvestmentScreening.Equipment.field.personnelPerShift')}\n${t('features.InvestmentScreening.Equipment.field.technician')}`,
        t('features.InvestmentScreening.Equipment.field.personnelWithoutEquipPerShift'),
        t('features.InvestmentScreening.Equipment.field.equipmentCategory'),
        t('features.InvestmentScreening.Equipment.field.supplier')
      ];

      // 「名称 (ID)」形式の文字列を生成するヘルパー関数
      const formatNameWithId = (name?: string | null, id?: string | null): string => {
        if (name && id) {
          return `${name} (${id})`;
        }
        return "";
      };

      // 画面表示中のデータを連携
      let values: string[][] = [];
      values = equipmentData.slice(0, equipmentCount).filter(e => !e.isReadonly && !isModifiedRow(e)).map((equipment =>
        [
          equipment.equipmentName ?? "",
          String(equipment.unitPrice ?? ""),
          equipment.currencyName ?? "",
          String(equipment.quantity ?? ""),
          toSpreadsheetUnitLabel(equipment.quantityUnit),
          String(equipment.depreciationPeriod ?? ""),
          padYearMonth(equipment.applicationPlannedMonth ?? ""),
          padYearMonth(equipment.acceptanceMonth ?? ""),
          padYearMonth(equipment.depreciationStartMonth ?? ""),
          String(equipment.unskilledWorker ?? ""),
          String(equipment.multiSkilledWorker ?? ""),
          String(equipment.technician ?? ""),
          String(equipment.numberOfPeopleRequiredForWithoutEquipment ?? ""),
          formatNameWithId(getMultilingualDisplayName(equipment.equipmentGroupName), equipment.equipmentGroupId),
          formatNameWithId(getMultilingualDisplayName(equipment.equipmentSupplierName), equipment.equipmentSupplierId)
        ]
      ));

      // rangeと二次元配列の大きさを合わせる必要がある。
      const emptyValues = Array.from({ length: MAX_EQUIPMENT_COUNT + 1 }, () => Array(15).fill(""));

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // スプレッドシート情報を取得してシートIDを取得
      const spreadSheetInfo = await googleAPI.getSpreadSheetInfo(activeSpreadsheetId);
      const sheetId = spreadSheetInfo.sheets?.[0]?.properties?.sheetId ?? 0;
      // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
      await googleAPI.resetSheetFormatAndValidation(activeSpreadsheetId, [sheetId]);

      // 日付列をテキスト形式に設定（データ書き込み前に設定）
      // G, H, I列（申請予定月、検収月、償却開始月）
      const dateColumns = [7, 8, 9];
      for (const col of dateColumns) {
        try {
          await googleAPI.spreadSheetSetCellFormat(activeSpreadsheetId, [{
            sheetId: sheetId,
            startRow: 2,
            endRow: MAX_EQUIPMENT_COUNT + 2,
            startCol: col,
            endCol: col,
            format: {
              numberFormat: { type: "TEXT", pattern: "@" }
            }
          }]);
        } catch (e) {
          console.warn(`Failed to set text format for column ${col}`, e);
        }
      }

      // 既存データがあれば、初期化
      await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 1, emptyValues);

      // Spreadシートにデータを反映
      await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 1, [...[HEADER], ...values]);

      // 設備分類のドロップダウン選択肢を生成（全件から生成：候補も含む）
      const groupOptions = equipmentGroups
        .filter(g => !g.deleted)
        .map(g => `${getMultilingualDisplayName(g.equipmentGroupName)} (${g.equipmentGroupId})`);

      // サプライヤーのドロップダウン選択肢を生成
      const supplierOptions = equipmentSuppliers
        .filter(s => !s.deleted)
        .map(s => `${getMultilingualDisplayName(s.equipmentSupplierName)} (${s.equipmentSupplierId})`);

      // 通貨のドロップダウン選択肢を生成
      const currencyNameList = currencyData
        .filter(c => c.currencyName)
        .map(c => c.currencyName!);

      // ドロップダウンリストを設定（2行目からMAX_EQUIPMENT_COUNT+1行目まで）
      if (groupOptions.length > 0 || supplierOptions.length > 0 || currencyNameList.length > 0) {
        const validationRanges: { sheetId: number; startRow: number; endRow: number; startCol: number; endCol: number; values: string[]; strict: boolean }[] = [];

        if (groupOptions.length > 0) {
          validationRanges.push({
            sheetId: sheetId,
            startRow: 2,
            endRow: MAX_EQUIPMENT_COUNT + 2,
            startCol: 14,  // N列（設備分類）
            endCol: 14,
            values: groupOptions,
            strict: false  // 選択肢以外も入力可能
          });
        }

        if (supplierOptions.length > 0) {
          validationRanges.push({
            sheetId: sheetId,
            startRow: 2,
            endRow: MAX_EQUIPMENT_COUNT + 2,
            startCol: 15,  // O列（サプライヤー）
            endCol: 15,
            values: supplierOptions,
            strict: false  // 選択肢以外も入力可能
          });
        }

        if (currencyNameList.length > 0) {
          validationRanges.push({
            sheetId: sheetId,
            startRow: 2,
            endRow: MAX_EQUIPMENT_COUNT + 2,
            startCol: 3,  // C列（通貨）
            endCol: 3,
            values: currencyNameList,
            strict: true  // 通貨マスタ登録済みの値のみ許可
          });
        }

        const quantityUnitOptions = QUANTITY_UNIT_KEYS.map(item => t(item.key));
        if (quantityUnitOptions.length > 0) {
          validationRanges.push({
            sheetId: sheetId,
            startRow: 2,
            endRow: MAX_EQUIPMENT_COUNT + 2,
            startCol: 5,  // E列（単位）
            endCol: 5,
            values: quantityUnitOptions,
            strict: false  // 選択肢以外も入力可能
          });
        }

        await googleAPI.spreadSheetSetDataValidation(activeSpreadsheetId, validationRanges);
      }
    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      spreadSheetWindowss?.close();
      setInputMode("manual");

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 「データ連携」ボタン押下
   * @returns
   */
  const handleSyncData = async () => {
    if (!clickedNode) {
      console.error("クリックされたノードが存在しません");
      return;
    }
    setIsLoading(true);

    try {
      const activeSpreadsheetId = clickedNode.spreadsheetId;
      if (!activeSpreadsheetId) {
        console.error("スプレッドシートIDがありません");
        return;
      }

      // アクセストークンリフレッシュ
      if (!await googleAPI.ensureValidToken()) {
        googleAPI.signIn();
      }

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;
      const activeRange = `${sheetName}!A1:O`;

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, activeRange);

      const { values } = result;
      if (values) {
        // 画面へ反映
        const converted = convertSheetToSelectedProcesses(values);
        setEquipmentData(converted);
        setEquipmentCount(converted.length);

        if (values.length > MAX_EQUIPMENT_COUNT + 1) {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.dataLinkSuccessOver', { max: MAX_EQUIPMENT_COUNT }),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.dataLinkSuccess'),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }

        // バリデーションエラーリセット
        setErrorsList(JSON.parse(JSON.stringify(defaultErrors)));
      }
    } catch (error) {
      console.error("データ連携エラー:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.dataLinkFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * YYYY-MM形式の年月バリデーション
   * @param value 年月文字列
   * @returns true: 有効、false: 無効
   */
  const isValidYearMonth = (value: string): boolean => {
    const yearMonthRegex = /^\d{4}-(0[1-9]|1[0-2])$/;
    return yearMonthRegex.test(value);
  };

  /**
   * Google Spreadシートデータを変換する。
   * @param values Google Spreadシートデータ
   * @param processId プロセスID（工程ID）
   * @param processName プロセス名（工程名）
   * @returns 変換後のデータ
   */
  const convertSheetToSelectedProcesses = (values: string[][]): EquipmentType[] => {
    // 読み取り専用データ+変更行を抽出
    const fixedData = equipmentData.filter(e => e.isReadonly || isModifiedRow(e));

    // 目的：「新製品」かそれ以外かでマージ先データの初期値を決める。
    // const result: EquipmentType[] = projectWithRdsData?.purpose === PurposeType.NewProduct.code ? [] : fixedData;
    const result: EquipmentType[] = fixedData;

    if (!values || values.length <= 1) {
      // データなし
      return result;
    }

    // ヘッダ部＋データ部を最大件数で切り出す。
    const limitValues = values.slice(0, MAX_EQUIPMENT_COUNT + 1);

    // Spreadシートデータで繰り返す
    limitValues.slice(1).forEach((row) => {
      const equipmentName = row[0] || "";
      // invalidFieldsオブジェクトを初期化
      const invalidFields: { [key: string]: string } = {};
      let unitPrice: number = 0;
      const currencyName = row[2] || "";
      let quantity: number = 0;
      let quantityUnit: string = row[4] || ""; 
      let depreciationPeriod: number = 0;
      let applicationPlannedMonth = padYearMonth(row[6]) || "";
      let acceptanceMonth = padYearMonth(row[7]) || "";
      let depreciationStartMonth = padYearMonth(row[8]) || "";
      let unskilledWorker: number = 0;
      let multiSkilledWorker: number = 0;
      let technician: number = 0;
      let numberOfPeopleRequiredForWithoutEquipment: number = 0;

      if (!equipmentName) {
        return; // break;
      }

      try {
        // 単価
        unitPrice = (Number((row[1] || "0").toString().replace(/,/g, "")));
        if (isNaN(unitPrice)) {
          unitPrice = 0;
          invalidFields["unitPrice"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (unitPrice <= 0) {
          unitPrice = 0;
          invalidFields["unitPrice"] = t('features.InvestmentScreening.Equipment.validation.positiveRequired');
        } else if (unitPrice > MAX_UNIT_PRICE) {
          unitPrice = MAX_UNIT_PRICE;
        }
      } catch (e) {
        unitPrice = 0;
        invalidFields["unitPrice"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      try {
        // 数量
        quantity = (Number((row[3] || "0").toString().replace(/,/g, "")));
        if (isNaN(quantity)) {
          quantity = 0;
          invalidFields["quantity"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (projectWithRdsData?.purpose === PurposeType.NewProduct.code || projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code) {
          // 新製品・増産の場合、0より大きい数値のみ
          if (quantity <= 0) {
            quantity = 0;
            invalidFields["quantity"] = t('features.InvestmentScreening.Equipment.validation.positiveRequired');
          } else if (quantity > MAX_QUANTITY) {
            quantity = MAX_QUANTITY;
          }
        } else {
          // SpreadSheetは新規設備行のみ編集対象のため、目的に関わらず0より大きい数値のみ
          if (quantity <= 0) {
            quantity = 0;
            invalidFields["quantity"] = t('features.InvestmentScreening.Equipment.validation.positiveRequired');
          } else if (quantity > MAX_QUANTITY) {
            quantity = MAX_QUANTITY;
          }
        }
      } catch (e) {
        quantity = 0;
        invalidFields["quantity"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      // 単位（E列：インデックス 4）
      try {
        const cellValue = (row[4] || "").toString().trim();
        // 空欄チェック
        if (!cellValue) {
          quantityUnit = ""; // 値を空にする
          invalidFields["quantityUnit"] = t('features.InvestmentScreening.Equipment.validation.required'); 
          // ※プロジェクト内で必須入力用に使われている翻訳キーを指定してください。
          // （例: 'features.InvestmentScreening.Equipment.validation.quantityUnitRequired' など、環境に合ったキーを使用してください）
        } else {
          // 入力がある場合のみ逆変換
          quantityUnit = toDbQuantityUnitCode(cellValue);
        }
      } catch (e) {
        quantityUnit = "";
        invalidFields["quantityUnit"] = t('features.InvestmentScreening.Equipment.validation.required');
      }

      try {
        // 償却年数
        depreciationPeriod = (Number((row[5] || "0").toString().replace(/,/g, "")));
        if (isNaN(depreciationPeriod)) {
          depreciationPeriod = 0;
          invalidFields["depreciationPeriod"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (depreciationPeriod > MAX_DEPRECIATION_PERIOD) {
          depreciationPeriod = MAX_DEPRECIATION_PERIOD;
        } else if (depreciationPeriod < 0) {
          depreciationPeriod = 0;
        }
      } catch (e) {
        depreciationPeriod = 0;
        invalidFields["depreciationPeriod"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      try {
        // 設備に必要な人員数（単純工）
        unskilledWorker = (Number((row[9] || "0").toString().replace(/,/g, "")));
        if (isNaN(unskilledWorker)) {
          unskilledWorker = 0;
          invalidFields["unskilledWorker"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (unskilledWorker > MAX_NUMBER_OF_PEOPLE_REQUIRED) {
          unskilledWorker = MAX_NUMBER_OF_PEOPLE_REQUIRED;
        }
      } catch (e) {
        unskilledWorker = 0;
        invalidFields["unskilledWorker"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      try {
        // 設備に必要な人員数（多能工）
        multiSkilledWorker = (Number((row[10] || "0").toString().replace(/,/g, "")));
        if (isNaN(multiSkilledWorker)) {
          multiSkilledWorker = 0;
          invalidFields["multiSkilledWorker"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (multiSkilledWorker > MAX_NUMBER_OF_PEOPLE_REQUIRED) {
          multiSkilledWorker = MAX_NUMBER_OF_PEOPLE_REQUIRED;
        }
      } catch (e) {
        multiSkilledWorker = 0;
        invalidFields["multiSkilledWorker"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      try {
        // 設備に必要な人員数（テクニシャン）
        technician = (Number((row[11] || "0").toString().replace(/,/g, "")));
        if (isNaN(technician)) {
          technician = 0;
          invalidFields["technician"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (technician > MAX_NUMBER_OF_PEOPLE_REQUIRED) {
          technician = MAX_NUMBER_OF_PEOPLE_REQUIRED;
        }
      } catch (e) {
        technician = 0;
        invalidFields["technician"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      try {
        // 設備なし時に必要な人員数
        numberOfPeopleRequiredForWithoutEquipment = (Number((row[12] || "0").toString().replace(/,/g, "")));
        console.log("numberOfPeopleRequiredForWithoutEquipment", numberOfPeopleRequiredForWithoutEquipment)
        if (isNaN(numberOfPeopleRequiredForWithoutEquipment)) {
          numberOfPeopleRequiredForWithoutEquipment = 0;
          invalidFields["numberOfPeopleRequiredForWithoutEquipment"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
        } else if (numberOfPeopleRequiredForWithoutEquipment > MAX_NUMBER_OF_PEOPLE_REQUIRED) {
          numberOfPeopleRequiredForWithoutEquipment = MAX_NUMBER_OF_PEOPLE_REQUIRED;
        }
      } catch (e) {
        numberOfPeopleRequiredForWithoutEquipment = 0;
        invalidFields["numberOfPeopleRequiredForWithoutEquipment"] = t('features.InvestmentScreening.Equipment.validation.numericRequired');
      }

      const tempData = equipmentData.filter(e => !e.isReadonly && !isModifiedRow(e));

      // 設備名が一致するものを取得
      const beforeData = tempData.find(equipment => equipment.equipmentName === equipmentName);

      // 通貨名から通貨IDを取得
      const currency = currencyData.find(c => c.currencyName === currencyName);
      // 通貨が見つからない場合は空に設定
      const validCurrencyId = currency ? currency.currencyId : null;
      const validCurrencyName = currency ? currency.currencyName : "";

      if (!currency && currencyName !== "") {
        invalidFields["currencyId"] = t('features.InvestmentScreening.Equipment.validation.invalidCurrency');
      }

      // 通貨に応じた単価の小数チェック
      if (currency && unitPrice > 0 && !hasDecimal(validCurrencyName ?? "") && unitPrice % 1 !== 0) {
        invalidFields["unitPrice"] = t('features.InvestmentScreening.Equipment.validation.integerOnlyForCurrency', { currencyName: validCurrencyName });
      }

      // 「名称 (ID)」形式からIDを抽出するヘルパー関数
      const extractIdFromNameWithId = (value: string): string | null => {
        if (!value) return null;
        const match = value.match(/\(([^)]+)\)$/);
        return match ? match[1] : null;
      };

      // 設備分類の処理（row[12]）
      const groupValue = row[13] || "";
      const groupId = extractIdFromNameWithId(groupValue);
      const group = groupId
        ? equipmentGroups.find(g => g.equipmentGroupId === groupId)
        : null;
      const validGroupId = group?.equipmentGroupId ?? (beforeData?.equipmentGroupId ?? "");
      const validGroupName = group?.equipmentGroupName ?? (beforeData?.equipmentGroupName ?? undefined);

      if (!group && groupValue !== "") {
        invalidFields["equipmentGroupId"] = t('features.InvestmentScreening.Equipment.validation.invalidEquipmentGroup');
      }

      // サプライヤーの処理（row[13]）
      const supplierValue = row[14] || "";
      const supplierId = extractIdFromNameWithId(supplierValue);
      const supplier = supplierId
        ? equipmentSuppliers.find(s => s.equipmentSupplierId === supplierId)
        : null;
      const validSupplierId = supplier?.equipmentSupplierId ?? (beforeData?.equipmentSupplierId ?? "");
      const validSupplierName = supplier?.equipmentSupplierName ?? (beforeData?.equipmentSupplierName ?? undefined);

      if (!supplier && supplierValue !== "") {
        invalidFields["equipmentSupplierId"] = t('features.InvestmentScreening.Equipment.validation.invalidSupplier');
      }

      // 申請予定月のフォーマットチェック
      if (applicationPlannedMonth && !isValidYearMonth(applicationPlannedMonth)) {
        invalidFields["applicationPlannedMonth"] = t('features.InvestmentScreening.Equipment.validation.yearMonthFormat');
        applicationPlannedMonth = "";
      }

      // 検収月のフォーマットチェック
      if (acceptanceMonth && !isValidYearMonth(acceptanceMonth)) {
        invalidFields["acceptanceMonth"] = t('features.InvestmentScreening.Equipment.validation.yearMonthFormat');
        acceptanceMonth = "";
      }

      // 償却開始月のフォーマットチェック
      if (depreciationStartMonth && !isValidYearMonth(depreciationStartMonth)) {
        invalidFields["depreciationStartMonth"] = t('features.InvestmentScreening.Equipment.validation.yearMonthFormat');
        depreciationStartMonth = "";
      }

      result.push({
        // 設備名が一致するものがあれば、設備IDを引き継ぐ
        equipmentId: beforeData?.equipmentId,
        equipmentName: equipmentName,
        unitPrice: unitPrice,
        quantity: quantity,
        quantityUnit: quantityUnit, // 数量単位
        depreciationPeriod: depreciationPeriod,
        depreciationStartMonth: depreciationStartMonth,
        applicationPlannedMonth: applicationPlannedMonth,
        acceptanceMonth: acceptanceMonth,
        unskilledWorker: unskilledWorker,
        multiSkilledWorker: multiSkilledWorker,
        technician: technician,
        numberOfPeopleRequiredForWithoutEquipment: numberOfPeopleRequiredForWithoutEquipment,
        currencyId: validCurrencyId,
        currencyName: validCurrencyName,
        // 編集項目ではないため、設備名が一致するものがあれば、引き継ぐ
        fixedAssetCode: (beforeData?.fixedAssetCode ?? ""),
        scheduledOrderDate: (beforeData?.scheduledOrderDate ?? ""),
        equipmentSupplierId: validSupplierId,
        equipmentSupplierName: validSupplierName,
        equipmentGroupId: validGroupId,
        equipmentGroupName: validGroupName,
        invalidFields: Object.keys(invalidFields).length > 0 ? invalidFields : undefined,
      });
    });

    // displayOrder再採番（processIdごと、固定データの最大値から連番）
    renumberDisplayOrder(result, isEmptyEquipment);

    return result;
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSaveAll: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    // フォーカスを外す
    event.currentTarget.blur();

    // 保存状態を保存中に更新
    setIsSaving(true);

    try {
      // 「行数」に応じた件数を抽出
      const tempEquipmentData = equipmentData.slice(0, equipmentCount);

      if (!validate(tempEquipmentData)) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.validation.hasErrors'),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return;
      }

      // 空データ以外+読み取り専用以外を抽出
      const validData = tempEquipmentData.filter(e => !isEmptyEquipment(e) && !e.isReadonly);

      // 登録パラメータ
      const input = {
        projectId: projectId,
        processId: clickedNode?.uniqueProcessId,
        equipments: validData.map((e) => {
          // トータル数量を計算
          const totalQuantity = calculateTotalQuantity(e, tempEquipmentData);
          return {
            equipmentId: e.equipmentId,
            equipmentName: e.equipmentName,
            fixedAssetCode: e.fixedAssetCode,
            scheduledOrderDate: e.scheduledOrderDate,
            unitPrice: e.unitPrice,
            quantity: e.quantity,
            totalQuantity: totalQuantity,
            quantityUnit: e.quantityUnit || "unit",
            currencyId: e.currencyId,
            amount: calcAmount(e.unitPrice, e.quantity, e)?.toFixed(2),
            numberOfPeopleRequiredForEquipment: (e.unskilledWorker || 0) + (e.multiSkilledWorker || 0) + (e.technician || 0),
            unskilledWorker: (e.unskilledWorker || 0),
            multiSkilledWorker: (e.multiSkilledWorker || 0),
            technician: (e.technician || 0),
            numberOfPeopleRequiredForWithoutEquipment: (e.numberOfPeopleRequiredForWithoutEquipment || 0),
            equipmentSupplierId: e.equipmentSupplierId,
            equipmentGroupId: e.equipmentGroupId,
            updateDate: e.updateDate,
            depreciationPeriod: e.depreciationPeriod,
            depreciationStartMonth: e.depreciationStartMonth,
            applicationPlannedMonth: e.applicationPlannedMonth,
            acceptanceMonth: e.acceptanceMonth,
            displayOrder: e.displayOrder,
          };
        }),
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putEquipment($input: ProcessEquipmentInput!) {
            putEquipment(input: $input) {status, message}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result.data.putEquipment.status === "success") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putEquipment.`);
        onCloseInputModal();

        // プロセスフロー描画
        await drawProcessFlowWithFetch();

        // ベンチマークデータの整合性チェック（目的が「増産」の場合のみ）
        if (projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code) {
          await cleanupBenchmarkData();
        }

        // 更新前後サマリーデータの整合性チェック（目的が「更新・その他」の場合のみ）
        if (projectWithRdsData?.purpose === PurposeType.UpdateAndOther.code) {
          await cleanupUpdateSummaryData();
        }

        // 合理化前後サマリーデータの整合性チェック（目的が「合理化」の場合のみ）
        if (projectWithRdsData?.purpose === PurposeType.Rationalization.code) {
          await cleanupRationalizationSummaryData();
        }

      } else if (result?.data?.putEquipment.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putEquipment.`);
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.saveFailedSimple'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putEquipment. errors = ${JSON.stringify(result?.errors)}`);
      }
    } catch (error) {
      console.error("保存失敗:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.saveFailedSimple'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      // 保存状態を未保存に更新
      setIsSaving(false);

      // 数量単位列の自由入力で設定した単位の履歴初期化
      setCustomUnits({});
      setCustomUnit("");
      setTargetRowIndex(null);
      setUnitSnap([]);
    }
  };

  /**
   * 設備入力のモーダルが閉じられた時
   */
  const handleCloseCompleteInputModal = async () => {
    // 入力モードを画面に戻す。
    setInputMode("manual");

    // 数量単位列の自由入力で設定した単位の履歴初期化
    setCustomUnits({});
    setCustomUnit("");
    setTargetRowIndex(null);
    setUnitSnap([]);

    // スプレッドシートウィンドウを閉じる。（spreadsheetId未設定時も開いているウィンドウは閉じる）
    if (spreadSheetWindowss && !spreadSheetWindowss.closed) {
      spreadSheetWindowss.close();
    }
  }

  /**
   * 設備入力モーダル: 一時保存（設備データをJSONファイルとしてダウンロード）
   * 保存対象: equipmentData（全行）, equipmentCount
   */
  const handleTempSaveInputModal = () => {
    const snapshot = { equipmentData, equipmentCount };
    const processId = clickedNode?.uniqueProcessId ?? "unknown";
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const json = JSON.stringify({ screenName: "EquipmentInputModal", processId, savedAt: new Date().toISOString(), data: snapshot }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EquipmentInputModal_${processId}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({
      title: t('features.InvestmentScreening.Equipment.field.equipmentInput'),
      description: t('features.InvestmentScreening.Equipment.message.tempSaved'),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  /**
   * 設備入力モーダル: 一時保存読み込み（JSONファイルを選択して設備データを復元）
   * 復元対象: equipmentData, equipmentCount
   */
  const handleTempLoadInputModal = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "EquipmentInputModal") {
            toast({ title: t('features.InvestmentScreening.Equipment.field.equipmentInput'), description: t('features.InvestmentScreening.Equipment.message.tempLoadScreenMismatch'), status: "error", duration: 4000, isClosable: true });
            return;
          }
          const { equipmentData: savedData, equipmentCount: savedCount } = parsed.data;
          setEquipmentData(savedData);
          setEquipmentCount(savedCount);
          setErrorsList(savedData.map(() => JSON.parse(JSON.stringify(defaultErrors))));
          toast({ title: t('features.InvestmentScreening.Equipment.field.equipmentInput'), description: t('features.InvestmentScreening.Equipment.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: t('features.InvestmentScreening.Equipment.field.equipmentInput'), description: t('features.InvestmentScreening.Equipment.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /**
   * バリデーションチェックを行う。
   * @param tempEquipmentData 設備情報
   * @returns
   */
  const validate = (tempEquipmentData: EquipmentType[]) => {
    let isError = false;

    const errorsList: Errors[] = [];

    for (let i = 0; i < tempEquipmentData.length; i++) {
      const equipment = tempEquipmentData[i];
      const errors: Errors = JSON.parse(JSON.stringify(defaultErrors));
      errorsList?.push(errors);

      if (isEmptyEquipment(equipment)) {
        // 空データ行はチェックしない。
        continue;
      }

      if (equipment.isReadonly) {
        continue;
      }

      // エラーメッセージ
      // 必須入力
      const requiredMessage = t('features.InvestmentScreening.Equipment.validation.required');
      // 年月 - 有効範囲（日付フォーマット済み）
      const _dateStart = dayjs(ValidDateRangeStart).format(t('common.dateFormat.dayjsYearMonth'));
      const _dateEnd = dayjs(ValidDateRangeEnd).format(t('common.dateFormat.dayjsYearMonth'));

      // 設備名
      if (isEmpty(equipment.equipmentName)) {
        errors.equipmentName.isError = true;
        errors.equipmentName.errorMessage = t('features.InvestmentScreening.Equipment.validation.equipmentNameRequired');
        isError = true;
      } else if (!isModifiedRow(equipment) && hasDuplicates("equipmentName", equipment.equipmentName ?? "", i)) {
        errors.equipmentName.isError = true;
        errors.equipmentName.errorMessage = t('features.InvestmentScreening.Equipment.validation.equipmentNameDuplicate');
        isError = true;
      }

      // 数量
      if (isEmpty(equipment.quantity)) {
        errors.quantity.isError = true;
        errors.quantity.errorMessage = t('features.InvestmentScreening.Equipment.validation.quantityRequired');
        isError = true;
      } else if (projectWithRdsData?.purpose === PurposeType.NewProduct.code || projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code) {
        // 新製品・増産の場合、0より大きい数値のみ
        if ((equipment.quantity ?? 0) <= 0) {
          errors.quantity.isError = true;
          errors.quantity.errorMessage = t('features.InvestmentScreening.Equipment.validation.quantityPositive');
          isError = true;
        }
      } else {
        // 合理化・更新・その他の場合、0以外を許容（マイナス値OK）
        if (equipment.quantity === 0) {
          errors.quantity.isError = true;
          errors.quantity.errorMessage = t('features.InvestmentScreening.Equipment.validation.quantityNonZero');
          isError = true;
        } else if (isModifiedRow(equipment) && calculateTotalQuantity(equipment, tempEquipmentData) < 0) {
          // 変更行: トータル数量がマイナスになる場合はエラー
          errors.quantity.isError = true;
          errors.quantity.errorMessage = t('features.InvestmentScreening.Equipment.validation.quantityBelowPrevious');
          isError = true;
        }
      }

      // 数量単位
      if (equipment.quantityUnit === "") {
        if (!errors.quantityUnit) {
          errors.quantityUnit = { isError: false, errorMessage: "" };
        }
        errors.quantityUnit.isError = true;
        errors.quantityUnit.errorMessage = t('features.InvestmentScreening.Equipment.validation.quantityUnitRequired') || requiredMessage;
        isError = true;
      } else if ((equipment.quantityUnit || "").toString().trim().length > 15) {
         errors.quantityUnit = {
          isError: true,
          errorMessage: t('components.EquipmentEditModal.validation.within15Chars'), 
        };
        isError = true;
      }

      // 単価
      if (isEmpty(equipment.unitPrice)) {
        errors.unitPrice.isError = true;
        errors.unitPrice.errorMessage = t('features.InvestmentScreening.Equipment.validation.unitPriceRequired');
        isError = true;
      } else if (equipment.currencyId) {
        // 通貨に応じた小数チェック
        const currencyName = currencyData.find(c => c.currencyId === equipment.currencyId)?.currencyName ?? "";
        if (!hasDecimal(currencyName) && (equipment.unitPrice ?? 0) % 1 !== 0) {
          errors.unitPrice.isError = true;
          errors.unitPrice.errorMessage = t('features.InvestmentScreening.Equipment.validation.unitPriceIntegerOnly', { currency: currencyName });
          isError = true;
        }
      }

      // 通貨
      if (isEmpty(equipment.currencyId)) {
        if (!errors.currencyId) {
          errors.currencyId = { isError: false, errorMessage: "" };
        }
        errors.currencyId.isError = true;
        errors.currencyId.errorMessage = t('features.InvestmentScreening.Equipment.validation.currencyRequired');
        isError = true;
      }

      // 償却年数
      if (isEmpty(equipment.depreciationPeriod)) {
        errors.depreciationPeriod.isError = true;
        errors.depreciationPeriod.errorMessage = t('features.InvestmentScreening.Equipment.validation.depreciationPeriodRequired');
        isError = true;
      }

      // 償却開始月
      // チェック用にYYYY-MM-DDの形式にしておく
      // ※年月だけでも可能だが、変な動作にならないよう明示的に形式を日まで含めた形にしておく
      const validDepStMon: string = `${equipment.depreciationStartMonth}-01`;
      if (isEmpty(equipment.depreciationStartMonth)) {
        // 必須チェック
        errors.depreciationStartMonth.isError = true;
        errors.depreciationStartMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.depreciationStartMonthRequired');
        isError = true;
      } else if (!isValidDateFormat(validDepStMon)) {
        // 日付フォーマット
        errors.depreciationStartMonth.isError = true;
        errors.depreciationStartMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.depreciationStartMonthFormat');
        isError = true;
      } else if (!isValidDateRange(new Date(validDepStMon))) {
        // 日付有効範囲
        errors.depreciationStartMonth.isError = true;
        errors.depreciationStartMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.depreciationStartMonthRange', { start: _dateStart, end: _dateEnd });
        isError = true;
      }

      // 検収月
      // チェック用にYYYY-MM-DDの形式にしておく
      // ※年月だけでも可能だが、変な動作にならないよう明示的に形式を日まで含めた形にしておく
      const validAcceptMon: string = `${equipment.acceptanceMonth}-01`;
      if (isEmpty(equipment.acceptanceMonth)) {
        errors.acceptanceMonth.isError = true;
        errors.acceptanceMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.acceptanceMonthRequired');
        isError = true;
      } else if (!isValidDateFormat(validAcceptMon)) {
        // 日付フォーマット
        errors.acceptanceMonth.isError = true;
        errors.acceptanceMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.acceptanceMonthFormat');
        isError = true;
      } else if (!isValidDateRange(new Date(validAcceptMon))) {
        // 日付有効範囲
        errors.acceptanceMonth.isError = true;
        errors.acceptanceMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.acceptanceMonthRange', { start: _dateStart, end: _dateEnd });
        isError = true;
      }

      // 申請予定月
      // チェック用にYYYY-MM-DDの形式にしておく
      // ※年月だけでも可能だが、変な動作にならないよう明示的に形式を日まで含めた形にしておく
      const validApplicationPlannedMon: string = `${equipment.applicationPlannedMonth}-01`;
      if (isEmpty(equipment.applicationPlannedMonth)) {
        errors.applicationPlannedMonth.isError = true;
        errors.applicationPlannedMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.applicationPlannedMonthRequired');
        isError = true;
      } else if (!isValidDateFormat(validApplicationPlannedMon)) {
        // 日付フォーマット
        errors.applicationPlannedMonth.isError = true;
        errors.applicationPlannedMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.applicationPlannedMonthFormat');
        isError = true;
      } else if (!isValidDateRange(new Date(validApplicationPlannedMon))) {
        // 日付有効範囲
        errors.applicationPlannedMonth.isError = true;
        errors.applicationPlannedMonth.errorMessage = t('features.InvestmentScreening.Equipment.validation.applicationPlannedMonthRange', { start: _dateStart, end: _dateEnd });
        isError = true;
      }
    };

    setErrorsList(errorsList);

    return !isError;
  }

  /**
   * 値入力ありかを判定する。
   * @param value 値
   * @returns true：データあり、false：空データ
   */
  const isEmpty = (value?: any | null) => {
    if (typeof value === "string") {
      return (value ?? "") === "";
    } else if (typeof value === "number") {
      return value === undefined || value === null || Number.isNaN(value);
    }

    return !value;
  }

  /**
   * 指定したプロパティに重複した値がないか検証する。
   * @param property プロパティ
   * @param value 検証値
   * @param index インデックス
   * @returns true：重複あり、false：重複なし
   */
  const hasDuplicates = (property: keyof EquipmentType, value: string, index: number) => {
    let i = 0;
    for (i = 0; i < equipmentData.length; i++) {
      if (i === index) {
        continue;
      }

      const equipment = equipmentData[i];

      if (isModifiedRow(equipment)) {
        continue;
      }

      const propertyValue = equipment[property] as string ?? "";

      if (propertyValue === value) {
        return true; // 重複が見つかれば、すぐにtrueを返す
      }
    };

    return false;
  }

  /**
   * 設備情報が空データであるか判定する。
   * @param equipment 設備情報
   * @returns true：空データ、false：データあり
   */
  const isEmptyEquipment = (equipment: EquipmentType) => {
    if (isEmpty(equipment.equipmentName) &&
      isEmpty(equipment.quantity) &&
      isEmpty(equipment.unitPrice)) {
      return true;
    }

    return false;
  }

  /**
   * 設備情報をコピー追加する。
   * @param equipment ベース設備情報
   * @param idx インデックス
   */
  const handleAddEquipment = (equipment: EquipmentType, idx: number) => {
    try {
      const baseId = getBaseId(equipment.equipmentId!);
      const regex = new RegExp(`^${baseId}(?:-(\\d{3}))?$`);

      let adjustIdx = 0;

      const maxBrNo = equipmentData.reduce((max, item) => {
        const match = item.equipmentId?.match(regex);
        if (match) {
          adjustIdx++;
          const branchStr = match[1] ?? "000";
          const branchNum = parseInt(branchStr, 10);
          return Math.max(max, branchNum);
        }
        return max;
      }, 0);

      const targetEquipmentId = maxBrNo === 0 ? baseId : `${baseId}-${(maxBrNo).toString().padStart(3, "0")}`;
      const maxBrNoIdx = equipmentData.findIndex(e => e.equipmentId === targetEquipmentId);

      equipment = equipmentData.find(e => e.equipmentId === targetEquipmentId)!;

      // 追加位置補正
      idx = maxBrNoIdx + 1;

      const newEquipment: EquipmentType = JSON.parse(JSON.stringify(equipment));
      newEquipment.equipmentId = `${baseId}-${(maxBrNo + 1).toString().padStart(3, "0")}`;
      // newEquipment.equipmentName = newEquipment.equipmentId; // デバッグ用（「設備名」列に「設備ID」を表示）
      newEquipment.isReadonly = false;
      newEquipment.quantity = undefined;
      // 枝番のdisplayOrder: ベースIDの整数部 + 0.001 * 枝番番号
      newEquipment.displayOrder = Math.floor(equipment.displayOrder || 0) + ((maxBrNo + 1) * 0.001);

      setEquipmentData(prevState => [
        ...prevState.slice(0, idx),
        newEquipment,
        ...prevState.slice(idx),
      ]);

      // 設備情報数を増やす
      setEquipmentCount(equipmentCount + 1);

      // エラー情報同期（追加）
      errorsList.splice(idx, 0, JSON.parse(JSON.stringify(defaultErrors)));
      setErrorsList(errorsList);

    } catch (e) {
      console.error(`handleAddEquipment exception`, e);
    }
  }

  /**
   * 指定した設備情報を削除する。
   * @param equipment 設備情報
   * @param idx インデックス
   * @returns
   */
  const handleRemoveEquipment = (equipment: EquipmentType, idx: number) => {
    try {
      setEquipmentData(prevState => [
        ...prevState.slice(0, idx),
        ...prevState.slice(idx + 1),
      ]);

      // 設備情報数を減らす
      setEquipmentCount(equipmentCount - 1);

      // エラー情報同期（削除）
      errorsList.splice(idx, 1);
      setErrorsList(errorsList);
    } catch (e) {
      console.error(`handleRemoveEquipment exception`, e);
    }
  }

  /** *********************************************************************************************************
   * 設備サプライヤー選択モーダル 関連
   ********************************************************************************************************* */
  /**
   * サプライヤー情報を設定する。
   * @param supplier サプライヤー情報
   */
  const handleSelectSupplier = (supplier: EquipmentSupplierType) => {

    changeEquipment(selectedIndex, "equipmentSupplierId", supplier.equipmentSupplierId);
    changeEquipment(selectedIndex, "equipmentSupplierName", supplier.equipmentSupplierName);
    onCloseSupplierModal();
  };

  /**
   * サプライヤーの仮登録
   * @param name サプライヤー名
   * @param nameEn サプライヤー名（英語）
   * @returns 登録されたサプライヤーデータ or null（失敗時）
   */
  const handleRegisterSupplierCandidate = async (name: string, nameEn: string): Promise<EquipmentSupplierType | null> => {
    try {
      const result = await WebAPI.request({
        query: `
          mutation putEquipmentSupplierCandidate($input: EquipmentSupplierCandidateInput!) {
            putEquipmentSupplierCandidate(input: $input) {status, message, id}
          }
        `,
        variables: {
          input: {
            equipmentSupplierName: { ja: name, en: nameEn },
          },
        }
      });

      if (result?.data?.putEquipmentSupplierCandidate?.status === 'success') {
        const newId = result.data.putEquipmentSupplierCandidate.id;
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.candidateRegistered', { name }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // サプライヤー一覧を再取得
        const refreshPayload = {
          query: `
            query {
              getEquipmentSupplier(hasDeleted: false) {
                equipmentSupplierId
                equipmentSupplierName { ja en }
                deleted
                isCandidate
                registeredBy
              }
            }`
        };
        const refreshResponse = await WebAPI.request(refreshPayload);
        if (refreshResponse?.data?.getEquipmentSupplier) {
          setEquipmentSuppliers(refreshResponse.data.getEquipmentSupplier);
        }

        return {
          equipmentSupplierId: newId,
          equipmentSupplierName: { ja: name, en: nameEn },
          isCandidate: true,
        };
      } else {
        const apiMessage = result?.data?.putEquipmentSupplierCandidate?.message;
        toast({
          title: TITLE,
          description: apiMessage || t('features.InvestmentScreening.Equipment.message.supplierCandidateError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return null;
      }
    } catch (e) {
      console.error('handleRegisterSupplierCandidate error:', e);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.supplierCandidateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  };

  /** *********************************************************************************************************
   * 設備サプライヤー選択モーダル 関連
   ********************************************************************************************************* */
  const handleSelectGroup = (group: EquipmentGroupType) => {
    changeEquipment(selectedIndex, "equipmentGroupId", group.equipmentGroupId);
    changeEquipment(selectedIndex, "equipmentGroupName", group.equipmentGroupName);
    onCloseGroupModal();
  };

  /**
   * 装置・機械の仮登録（必要に応じて設備分類も仮登録）
   * @param name 装置・機械名
   * @param categoryId 設備分類ID（空文字の場合は新規カテゴリ作成）
   * @param newCategoryName 新規設備分類名（categoryIdが空の場合に使用）
   * @returns 登録された装置・機械データ or null（失敗時）
   */
  const handleRegisterCandidate = async (name: string, nameEn: string, categoryId: string, newCategoryName?: string, newCategoryPrefix?: string, newCategoryNameEn?: string): Promise<EquipmentGroupType | null> => {
    try {
      let finalCategoryId = categoryId;

      // 新規設備分類の仮登録が必要な場合
      if (!categoryId && newCategoryName) {
        const catResult = await WebAPI.request({
          query: `
            mutation putEquipmentCategoryCandidate($input: EquipmentCategoryCandidateInput!) {
              putEquipmentCategoryCandidate(input: $input) {status, message, id}
            }
          `,
          variables: {
            input: {
              equipmentCategoryName: { ja: newCategoryName, en: newCategoryNameEn },
              codePrefix: newCategoryPrefix,
            },
          }
        });

        if (catResult?.data?.putEquipmentCategoryCandidate?.status === 'success') {
          finalCategoryId = catResult.data.putEquipmentCategoryCandidate.id;
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.categoryCandidateRegistered', { name: newCategoryName }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          const apiMessage = catResult?.data?.putEquipmentCategoryCandidate?.message;
          toast({
            title: TITLE,
            description: apiMessage || t('features.InvestmentScreening.Equipment.message.categoryCandidateFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return null;
        }
      }

      // 装置・機械の仮登録
      const result = await WebAPI.request({
        query: `
          mutation putEquipmentGroupCandidate($input: EquipmentGroupCandidateInput!) {
            putEquipmentGroupCandidate(input: $input) {status, message, id}
          }
        `,
        variables: {
          input: {
            equipmentGroupName: { ja: name, en: nameEn },
            equipmentCategoryId: finalCategoryId,
          },
        }
      });

      if (result?.data?.putEquipmentGroupCandidate?.status === 'success') {
        const newId = result.data.putEquipmentGroupCandidate.id;
        // セッション内の仮登録IDを追跡（ドロップダウン表示用）
        setRegisteredCandidateIds(prev => new Set(prev).add(newId));
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.candidateRegistered', { name }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // 設備分類一覧と主要装置・機械一覧を再取得
        const refreshPayload = {
          query: `
            query {
              getEquipmentCategory(hasDeleted: false) {
                equipmentCategoryId
                equipmentCategoryName { ja en }
                description
                codePrefix
                sortOrder
                isCandidate
              }
              getEquipmentGroup(hasDeleted: false) {
                equipmentGroupId
                equipmentGroupName { ja en }
                equipmentCategoryId
                sortOrder
                isCandidate
              }
            }`
        };
        const refreshResponse = await WebAPI.request(refreshPayload);
        if (refreshResponse?.data?.getEquipmentCategory) {
          setEquipmentCategories(refreshResponse.data.getEquipmentCategory);
        }
        if (refreshResponse?.data?.getEquipmentGroup) {
          refreshResponse.data.getEquipmentGroup.sort((a: EquipmentGroupType, b: EquipmentGroupType) =>
            a.equipmentGroupId?.localeCompare(b?.equipmentGroupId || "")
          );
          setEquipmentGroups(refreshResponse.data.getEquipmentGroup);
        }

        // 登録されたデータを返す
        return {
          equipmentGroupId: newId,
          equipmentGroupName: { ja: name, en: nameEn },
          equipmentCategoryId: finalCategoryId,
          isCandidate: true,
        };
      } else {
        const apiMessage = result?.data?.putEquipmentGroupCandidate?.message;
        toast({
          title: TITLE,
          description: apiMessage || t('features.InvestmentScreening.Equipment.message.machineCandidateFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        if (newCategoryName) {
          // 新規カテゴリは作成済みだが装置・機械の登録に失敗した場合
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.categoryAlreadyRegistered', { name: newCategoryName }),
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
          // 設備分類一覧を再取得して新規カテゴリをドロップダウンに反映
          const refreshCatPayload = {
            query: `query { getEquipmentCategory(hasDeleted: false) { equipmentCategoryId equipmentCategoryName { ja en } description codePrefix sortOrder isCandidate } }`
          };
          const refreshCatResponse = await WebAPI.request(refreshCatPayload);
          if (refreshCatResponse?.data?.getEquipmentCategory) {
            setEquipmentCategories(refreshCatResponse.data.getEquipmentCategory);
          }
        }
        return null;
      }
    } catch (error) {
      console.error("Exception to call API putEquipmentGroupCandidate. error = ", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.machineCandidateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  };

  /** *********************************************************************************************************
   * 過去ベストベンチマーク 関連
   ********************************************************************************************************* */
  /**
   * 過去ベストベンチマークボタンクリックハンドラー
   */
  const handleOpenBenchmarkModal = async () => {
    // 設備情報一覧取得
    await getEquipmentInfoList();

    // モーダル表示（設備投資合計金額はuseEffectで自動計算される）
    onOpenBenchmarkModal();
  };

  /**
   * 過去ベストベンチマークデータ保存ハンドラー
   */
  const handleSaveBenchmarkData = async (data: ProductionCapacityBenchmark[]) => {
    setBenchmarkData(data);
    onCloseBenchmarkModal();
  };

  /**
   * 更新前後サマリーデータ保存ハンドラー
   */
  const handleSaveUpdateSummaryData = async (data: UpdateSummaryInputData[]) => {
    setUpdateSummaryData(data);
    onCloseUpdateSummaryModal();
  };

  /**
   * 合理化前後サマリーデータ保存ハンドラー
   */
  const handleSaveRationalizationSummaryData = async (data: RationalizationSummaryInputData[]) => {
    setRationalizationSummaryData(data);
    onCloseRationalizationSummaryModal();
  };

  /**
   * 更新前後サマリーデータの整合性チェック・削除
   * 設備が削除されて、そのプロセスに設備が1件もなくなった場合、
   * そのプロセスに紐づく更新前後サマリーデータをDBから削除する
   */
  const cleanupUpdateSummaryData = async () => {
    try {
      // 現在のプロジェクトの全設備を取得
      const equipmentPayload = {
        query: `
          query getEquipmentByProjectId($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              processId
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const equipmentResponse = await WebAPI.request(equipmentPayload);

      if (!equipmentResponse?.data?.getEquipmentByProjectId) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.updateSummaryCheckFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getEquipmentByProjectId. errors = `, equipmentResponse?.errors);
        return;
      }

      // 設備が存在するprocessIdのセットを作成
      const existingProcessIds = new Set<string>(
        equipmentResponse.data.getEquipmentByProjectId
          .map((e: EquipmentType) => e.processId)
          .filter(Boolean)
      );

      // 更新前後サマリーデータを取得
      const additionalInfoPayload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              updateSummaryData
              updateDate
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const additionalInfoResponse = await WebAPI.request(additionalInfoPayload);

      if (!additionalInfoResponse?.data?.getProjectAdditionalInfoById) {
        // プロジェクト追加情報がない場合は正常（更新前後サマリーが登録されていない）
        console.log(`cleanupUpdateSummaryData: No additional info found`);
        return;
      }

      const projectAdditionalInfo = additionalInfoResponse.data.getProjectAdditionalInfoById;

      if (!projectAdditionalInfo.updateSummaryData) {
        // 更新前後サマリーデータがない場合は何もしない
        return;
      }

      // JSONパース
      const currentUpdateSummaryList: UpdateSummaryInputData[] = JSON.parse(projectAdditionalInfo.updateSummaryData);

      // 存在するprocessIdに紐づくサマリーのみをフィルタリング
      const filteredSummaryList = currentUpdateSummaryList.filter(
        (summary) => existingProcessIds.has(summary.productionLineId || "")
      );

      // データが減った場合のみ再保存
      if (filteredSummaryList.length < currentUpdateSummaryList.length) {
        const removedCount = currentUpdateSummaryList.length - filteredSummaryList.length;
        console.log(`cleanupUpdateSummaryData: Removing ${removedCount} orphaned update summary entries`);

        // 更新前後サマリーデータを再保存
        const putInput = {
          projectId: projectId,
          updateSummaryData: JSON.stringify(filteredSummaryList.map(({ projectId: _, ...rest }: any) => rest)),
          updateDate: projectAdditionalInfo.updateDate,
          ...(reviewDate && { reviewDate }),
        };

        const putResult = await WebAPI.request({
          query: `
            mutation putUpdateSummary($input: UpdateSummaryInput!) {
              putUpdateSummary(input: $input) {status}
            }
          `,
          variables: {
            input: putInput,
          }
        });

        if (putResult?.data?.putUpdateSummary?.status === "success") {
          // ステートを更新
          setUpdateSummaryData(filteredSummaryList);
          console.log(`Success to call API putUpdateSummary.`);
        } else if (putResult?.data?.putUpdateSummary?.status === 'expired') {
          toast({
            title: TITLE,
            description: Messages.expiredMessage,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          console.warn(`Conflict error. putUpdateSummary.`);
        } else {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.updateSummaryCheckFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error(`Failed to call API putUpdateSummary. errors = `, putResult?.errors);
        }
      }
    } catch (error) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.updateSummaryCheckFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putUpdateSummary. errors = `, error);
    }
  };

  /**
   * 合理化前後サマリーデータの整合性チェック・削除
   * 設備が削除されて、そのプロセスに設備が1件もなくなった場合、
   * そのプロセスに紐づく合理化前後サマリーデータをDBから削除する
   */
  const cleanupRationalizationSummaryData = async () => {
    try {
      // 現在のプロジェクトの全設備を取得
      const equipmentPayload = {
        query: `
          query getEquipmentByProjectId($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              processId
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const equipmentResponse = await WebAPI.request(equipmentPayload);

      if (!equipmentResponse?.data?.getEquipmentByProjectId) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.rationalizationCheckFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getEquipmentByProjectId. errors = `, equipmentResponse?.errors);
        return;
      }

      // 設備が存在するprocessIdのセットを作成
      const existingProcessIds = new Set<string>(
        equipmentResponse.data.getEquipmentByProjectId
          .map((e: EquipmentType) => e.processId)
          .filter(Boolean)
      );

      // 合理化前後サマリーデータを取得
      const additionalInfoPayload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              rationalizationSummaryData
              updateDate
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const additionalInfoResponse = await WebAPI.request(additionalInfoPayload);

      if (!additionalInfoResponse?.data?.getProjectAdditionalInfoById) {
        // プロジェクト追加情報がない場合は正常（合理化前後サマリーが登録されていない）
        console.log(`cleanupRationalizationSummaryData: No additional info found`);
        return;
      }

      const projectAdditionalInfo = additionalInfoResponse.data.getProjectAdditionalInfoById;

      if (!projectAdditionalInfo.rationalizationSummaryData) {
        // 合理化前後サマリーデータがない場合は何もしない
        return;
      }

      // JSONパース
      const currentRationalizationSummaryList: RationalizationSummaryInputData[] = JSON.parse(projectAdditionalInfo.rationalizationSummaryData);

      // 存在するprocessIdに紐づくサマリーのみをフィルタリング
      const filteredSummaryList = currentRationalizationSummaryList.filter(
        (summary) => existingProcessIds.has(summary.processId || "")
      );

      // データが減った場合のみ再保存
      if (filteredSummaryList.length < currentRationalizationSummaryList.length) {
        const removedCount = currentRationalizationSummaryList.length - filteredSummaryList.length;
        console.log(`cleanupRationalizationSummaryData: Removing ${removedCount} orphaned rationalization summary entries`);

        // 合理化前後サマリーデータを再保存
        const putInput = {
          projectId: projectId,
          rationalizationSummaryData: JSON.stringify(filteredSummaryList),
          updateDate: projectAdditionalInfo.updateDate,
          ...(reviewDate && { reviewDate }),
        };

        const putResult = await WebAPI.request({
          query: `
            mutation putRationalizationSummary($input: RationalizationSummaryInput!) {
              putRationalizationSummary(input: $input) {status}
            }
          `,
          variables: {
            input: putInput,
          }
        });

        if (putResult?.data?.putRationalizationSummary?.status === "success") {
          // ステートを更新
          setRationalizationSummaryData(filteredSummaryList);
          console.log(`Success to call API putRationalizationSummary.`);
        } else if (putResult?.data?.putRationalizationSummary?.status === 'expired') {
          toast({
            title: TITLE,
            description: Messages.expiredMessage,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          console.warn(`Conflict error. putRationalizationSummary.`);
        } else {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.rationalizationCheckFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error(`Failed to call API putRationalizationSummary. errors = `, putResult?.errors);
        }
      }
    } catch (error) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.rationalizationCheckFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putRationalizationSummary. errors = `, error);
    }
  };

  /**
   * ベンチマークデータの整合性チェック・削除
   * 設備が削除された場合、その設備IDに紐づくベンチマークデータをDBから削除する
   * ※ inputType が "equipment" の場合のみ対象
   */
  const cleanupBenchmarkData = async () => {
    try {
      // 現在のプロジェクトの全設備を取得
      const equipmentPayload = {
        query: `
          query getEquipmentByProjectId($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const equipmentResponse = await WebAPI.request(equipmentPayload);

      if (!equipmentResponse?.data?.getEquipmentByProjectId) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Equipment.message.benchmarkCheckFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getEquipmentByProjectId. errors = `, equipmentResponse?.errors);
        return;
      }

      // 設備IDのセットを作成
      const existingEquipmentIds = new Set<string>(
        equipmentResponse.data.getEquipmentByProjectId
          .map((e: EquipmentType) => e.equipmentId)
          .filter(Boolean)
      );

      // ベンチマークデータを取得
      const additionalInfoPayload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              benchmarkData
              updateDate
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      };
      const additionalInfoResponse = await WebAPI.request(additionalInfoPayload);

      if (!additionalInfoResponse?.data?.getProjectAdditionalInfoById) {
        // プロジェクト追加情報がない場合は正常（ベンチマークが登録されていない）
        console.log(`cleanupBenchmarkData: No additional info found`);
        return;
      }

      const projectAdditionalInfo = additionalInfoResponse.data.getProjectAdditionalInfoById;

      if (!projectAdditionalInfo.benchmarkData) {
        // ベンチマークデータがない場合は何もしない
        return;
      }

      // JSONパース
      const currentBenchmarkList: ProductionCapacityBenchmark[] = JSON.parse(projectAdditionalInfo.benchmarkData);

      // inputType が "line" の場合: 同一 productionLineId の重複を1件（末尾）に絞る
      if (currentBenchmarkList.length > 0 && currentBenchmarkList[0].inputType === "line") {
        // productionLineId ごとに最後に出現したもののみ残す
        const lineMap = new Map<string, ProductionCapacityBenchmark>();
        currentBenchmarkList.forEach(item => {
          lineMap.set(item.productionLineId || "", item);
        });
        const filteredBenchmarkList = Array.from(lineMap.values());

        if (filteredBenchmarkList.length < currentBenchmarkList.length) {
          const removedCount = currentBenchmarkList.length - filteredBenchmarkList.length;
          console.log(`cleanupBenchmarkData: Removing ${removedCount} duplicate line benchmark entries`);

          const putInput = {
            projectId: projectId,
            benchmarkData: JSON.stringify(filteredBenchmarkList.map(({ projectId: _, ...rest }: any) => rest)),
            updateDate: projectAdditionalInfo.updateDate,
            ...(reviewDate && { reviewDate }),
          };

          const putResult = await WebAPI.request({
            query: `
              mutation putBenchmark($input: BenchmarkInput!) {
                putBenchmark(input: $input) {status}
              }
            `,
            variables: { input: putInput },
          });

          if (putResult?.data?.putBenchmark?.status === "success") {
            setBenchmarkData(filteredBenchmarkList);
            console.log(`cleanupBenchmarkData: Successfully removed duplicate line benchmark entries`);
          } else if (putResult?.data?.putBenchmark?.status === 'expired') {
            toast({
              title: TITLE,
              description: Messages.expiredMessage,
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
            console.warn(`Conflict error. putBenchmark.`);
          } else {
            toast({
              title: TITLE,
              description: t('features.InvestmentScreening.Equipment.message.benchmarkCheckFailed'),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            console.error(`Failed to call API putBenchmark. errors = `, putResult?.errors);
          }
        }
        return;
      }

      // 存在する設備IDに紐づくベンチマークのみをフィルタリング
      const filteredBenchmarkList = currentBenchmarkList.filter(
        (benchmark) => existingEquipmentIds.has(benchmark.equipmentId || "")
      );

      // データが減った場合のみ再保存
      if (filteredBenchmarkList.length < currentBenchmarkList.length) {
        const removedCount = currentBenchmarkList.length - filteredBenchmarkList.length;
        console.log(`cleanupBenchmarkData: Removing ${removedCount} orphaned benchmark entries`);

        // ベンチマークデータを再保存
        const putInput = {
          projectId: projectId,
          benchmarkData: JSON.stringify(filteredBenchmarkList.map(({ projectId: _, ...rest }: any) => rest)),
          updateDate: projectAdditionalInfo.updateDate,
          ...(reviewDate && { reviewDate }),
        };

        const putResult = await WebAPI.request({
          query: `
            mutation putBenchmark($input: BenchmarkInput!) {
              putBenchmark(input: $input) {status}
            }
          `,
          variables: {
            input: putInput,
          }
        });

        if (putResult?.data?.putBenchmark?.status === "success") {
          // ステートを更新
          setBenchmarkData(filteredBenchmarkList);
          console.log(`Success to call API putBenchmark.`);
        } else if (putResult?.data?.putBenchmark?.status === 'expired') {
          toast({
            title: TITLE,
            description: Messages.expiredMessage,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          console.warn(`Conflict error. putBenchmark.`);
        } else {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.Equipment.message.benchmarkCheckFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error(`Failed to call API putBenchmark. errors = `, putResult?.errors);
        }
      }
    } catch (error) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Equipment.message.benchmarkCheckFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putBenchmark. errors = `, error);
    }
  };

  /**
   * ベンチマーク入力チェック
   * 全件登録必須チェック（過去ベストベンチマークを全設備分登録しないと通過できない仕様）は
   * 「1件以上必須チェック」のみ残す方針のためコメントアウト
   */
  // const [benchmarkStatus, setBenchmarkStatus] = useState<
  //   "ok" | "tooFew" | "tooMany" | "notRequired"
  // >("notRequired");


  // 初回＋設備保存時、設備データを取得
  useEffect(() => {
    getEquipmentInfoList();
  }, [isSaving]);

  // useEffect(() => {
  //   // 増産ならチェック開始
  //   if (projectWithRdsData?.purpose === "2") {
  //     // データがない場合は"tooFew"
  //     if (!benchmarkData || benchmarkData.length === 0) {
  //       setBenchmarkStatus("tooFew");
  //       return;
  //     }

  //     const inputType = benchmarkData[0].inputType;
  //     // ラインは１件であればOK
  //     if (inputType === "line" && benchmarkData.length === 1) {
  //       setBenchmarkStatus("ok");
  //     } else if (inputType === "equipment") {
  //       // equipmentId の一致をチェック
  //       const benchmarkIds = new Set(benchmarkData.map(b => b.equipmentId));
  //       const equipmentIds = new Set(equipmentListData.map(e => e.equipmentId));

  //       let allMatch = true;
  //       for (const id of equipmentIds) {
  //         if (!benchmarkIds.has(id ?? undefined)) {
  //           allMatch = false;
  //           break;
  //         }
  //       }

  //       if (allMatch && benchmarkData.length === equipmentListData.length) {
  //         setBenchmarkStatus("ok"); // 件数一致かつ全て一致
  //       } else if (!allMatch || benchmarkData.length < equipmentListData.length) {
  //         setBenchmarkStatus("tooFew"); // 過少または不一致
  //       } else {
  //         setBenchmarkStatus("tooMany"); // 過多
  //       }
  //     } else {
  //       setBenchmarkStatus("tooFew");
  //     }
  //   } else {
  //     setBenchmarkStatus("ok");
  //   }
  // }, [benchmarkData, equipmentListData, projectWithRdsData]);

  /** *********************************************************************************************************
* 更新前後サマリー 関連
********************************************************************************************************* */
  /**
   * 更新前後サマリーボタンクリックハンドラー
   */
  const handleOpenUpdateSummaryModal = async () => {
    // 設備情報一覧取得
    await getEquipmentInfoList();

    // モーダル表示（設備投資合計金額はuseEffectで自動計算される）
    onOpenUpdateSummaryModal();
  };

  /**
   * 合理化前後サマリーボタンクリックハンドラー
   */
  const handleOpenRationalizationSummaryModal = async () => {
    // 設備情報一覧取得
    await getEquipmentInfoList();

    // モーダル表示
    onOpenRationalizationSummaryModal();
  };

  /** *********************************************************************************************************
* 汎用設備チェックリスト 関連
********************************************************************************************************* */
  /**
   * 汎用設備チェックリストボタンクリックハンドラー
   */
  const handleOpenGeneralPurposeChecklistModal = () => {
    onOpenGeneralPurposeChecklistModal();
  };

  /**
   * 汎用設備チェックリストデータ保存ハンドラー
   */
  const handleSaveGeneralPurposeChecklistData = async (data: GeneralPurposeChecklistData) => {
    setGeneralPurposeChecklistData(data);
    onCloseGeneralPurposeChecklistModal();
  };

  /** *********************************************************************************************************
* 自由入力モーダル 関連
********************************************************************************************************* */
  /** 自由入力モーダルの開閉状態 */
  const [isCustomUnitModalOpen, setIsCustomUnitModalOpen] = useState(false);
  const [customUnit, setCustomUnit] = useState("");
  const [targetRowIndex, setTargetRowIndex] = useState<number | null>(null);
  const [customUnits, setCustomUnits] = useState<{ [key: number]: string[] }>({});
  const [unitSnap, setUnitSnap] = useState<EquipmentUnitSetting[]>([]);
  type EquipmentUnitSetting = {
    displayOrder: number | null;
    quantityUnit?: string | null;
  };
  /**
   * 項目選択変更ハンドラー
   */
  const handleUnitSelectChange = (rowIndex: number, newValue: any) => {
    if (!newValue) return;

    if (newValue === "custom") {
      setTargetRowIndex(rowIndex);
      // setCustomUnit(equipmentData[rowIndex]?.quantityUnit || "");
      setIsCustomUnitModalOpen(true);
    } else {
      if (newValue !== "unit" && newValue !== "set" && newValue !== "pcs") {
      setCustomUnits((prev) => {
        const currentList = prev[rowIndex] || [];
        if (!currentList.includes(newValue)) {
          return { ...prev, [rowIndex]: [...currentList, newValue] };
        }
        return prev;
        });
      };
      changeEquipment(rowIndex, "quantityUnit", newValue);
    }
  };

  const handleCustomUnitCancel = () => {
    setCustomUnit("");
    setTargetRowIndex(null);
    setIsCustomUnitModalOpen(false);
  };

  /**
   * 自由入力モーダルの確定ハンドラー
   */
  const handleCustomUnitConfirm = () => {
    if (targetRowIndex !== null && customUnit.trim()) {
      const trimmedUnit = customUnit.trim();

      //　固定選択肢（「台」「式」「個」等）に該当するか判定してコード化（例: "台" → "unit"）
      const resolvedUnit = toDbQuantityUnitCode(trimmedUnit);
      const standardUnits = ["unit", "set", "pcs"];
      // 配列に追加する形で保存（重複は含めない）
      if (!standardUnits.includes(resolvedUnit)) {
        setCustomUnits((prev) => {
          const currentList = prev[targetRowIndex] || [];
          const updatedList = currentList.includes(resolvedUnit)
            ? currentList
            : [...currentList, resolvedUnit];

          return {
            ...prev,
            [targetRowIndex]: updatedList,
          };
        });
      }
      changeEquipment(targetRowIndex, "quantityUnit", resolvedUnit);
      setCustomUnit("");
      setTargetRowIndex(null);
      setIsCustomUnitModalOpen(false);
    }
  };

  /**
   * レイアウト
   */
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      px={6}
      overflowY="auto"
      bg="white"
    >
      <fieldset disabled={!pagePermissions?.isEditable}>
        <Text mb="10px" color="blue">{t('features.InvestmentScreening.Equipment.label.projectName')} : {(projectWithRdsData?.revisedOpportunityNameByRevision ?? "") !== "" ? projectWithRdsData?.revisedOpportunityNameByRevision : projectWithRdsData?.opportunityName}</Text>
        <HStack align="start" mt={2}>
          <FormLabel
            mt={2}
            mr={1}
            style={{ whiteSpace: "nowrap", fontWeight: "bold" }}
          >
            {t('features.InvestmentScreening.Equipment.label.maxCycleTime')}
          </FormLabel>
          <Text
            mt={2}
            mr={1}
            w="fit-content"
            width="auto"
          >
            {`${Number(productionLine?.cycleTime || 0).toFixed(1)} ${t('features.InvestmentScreening.Equipment.label.secondUnit')}`}
          </Text>
          <Box mt={1}>
            <HelpIcon>
              <Text>
                {t('features.InvestmentScreening.Equipment.help.maxCycleTimeDesc')}
              </Text>
            </HelpIcon>
          </Box>

          <FormLabel
            mt={2}
            mr={1}
            ml={4}
            style={{ whiteSpace: "nowrap", fontWeight: "bold" }}
          >
            {t('features.InvestmentScreening.Equipment.label.yieldRate')}
          </FormLabel>
          <Text
            mt={2}
            mr={1}
            w="fit-content"
            width="auto"
          >
            {`${Number(productionLine?.yieldRate || 0).toFixed(2)} %`}
          </Text>
          <Box mt={1}>
            <HelpIcon>
              <Text>
                {t('features.InvestmentScreening.Equipment.help.yieldRateDesc')}
              </Text>
            </HelpIcon>
          </Box>

          {projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code && (
            <>
              <FormLabel
                mt={2}
                mr={1}
                ml={4}
                style={{ whiteSpace: "nowrap", fontWeight: "bold" }}
              >
                {t('features.InvestmentScreening.Equipment.label.bestProductionQuantity')}
              </FormLabel>
              <FormControl isInvalid={equipmentErrors.bestProductionQuantity.isError} w="fit-content" width="150px">
                <CustomNumberInput
                  decimalPlaces={2}
                  value={bestProductionQuantity ? Number(bestProductionQuantity) : null}
                  onChange={(value) => {
                    setBestProductionQuantity(value !== null ? String(value) : "0");
                  }}
                  onBlur={() => {
                    // 数値に変換
                    const parsedValue = Number(bestProductionQuantity);
                    // 最大値・最小値の制限を適用
                    const clampedValue = Math.min(Math.max(parsedValue, 0), 999999999999);
                    // 小数第一位表示に変換
                    const formattedValue = (clampedValue || 0).toFixed(0);
                    // useStateを更新
                    setBestProductionQuantity(formattedValue);
                    changeProductionLine("bestProductionQuantity", formattedValue);
                    // エラーチェック
                    if (Number(productionLine?.bestProductionQuantity || 0) <= 0) {
                      setEquipmentErrors((prevState) => ({
                        ...prevState,
                        bestProductionQuantity: {
                          isError: false,
                          errorMessage: "",
                        },
                      }));
                    }
                  }}
                  bg="white"
                />
                <FormErrorMessage>{equipmentErrors.bestProductionQuantity.errorMessage}</FormErrorMessage>
              </FormControl>
            </>
          )}
        </HStack>

        <Box height="calc(100vh - 310px)" mt={2} overflow="auto" border="1px solid #ccc">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodesDraggable={false}
            nodeTypes={nodeTypes}
            connectionLineType={ConnectionLineType.SmoothStep}
            minZoom={0.01}
            nodesConnectable={false}                  // 接続を無効化
            selectionOnDrag={false}                   // ドラッグ選択を無効化
            fitView
          />
        </Box>
      </fieldset>

      {/* 操作ボタン（1行目） */}
      <Flex justify="flex-end" mt="20px" wrap="wrap" rowGap="10px" align="center">
        <Box mr="10px">
          <HelpIcon>
            <Text>
              {t('features.InvestmentScreening.Equipment.help.processFlowInstruction')}<br />
              {t('features.InvestmentScreening.Equipment.help.equipmentDocsUploadDesc')}<br />
              {t('features.InvestmentScreening.Equipment.help.processFlowDesc1')}<br />
              {t('features.InvestmentScreening.Equipment.help.processFlowDesc2')}
            </Text>
          </HelpIcon>
        </Box>
        <Box>
          <Button
            colorScheme="blue"
            onClick={onOpenFileUploadModal}
            isDisabled={!pagePermissions?.isEditable}
          >
            {t('features.InvestmentScreening.Equipment.action.equipmentDocsUpload')}
          </Button>
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            onClick={onOpenImageUploadModal}
            isDisabled={!pagePermissions?.isEditable}
          >
            {t('features.InvestmentScreening.Equipment.action.equipmentImageUpload')}
          </Button>
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            onClick={handleProductListButtonClick}
          >
            {t('features.InvestmentScreening.Equipment.action.checklistInput')}
          </Button>
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            onClick={handleEquipmentListButtonClick}
          >
            {t('features.InvestmentScreening.Equipment.action.equipmentList')}
          </Button>
        </Box>
        {projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code &&
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              onClick={handleOpenBenchmarkModal}
            >
              {t('features.InvestmentScreening.Equipment.action.pastBestBenchmark')}
            </Button>
          </Box>
        }
        {projectWithRdsData?.purpose === PurposeType.UpdateAndOther.code &&
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              onClick={handleOpenUpdateSummaryModal}
            >
              {t('features.InvestmentScreening.Equipment.action.updateSummary')}
            </Button>
          </Box>
        }
        {projectWithRdsData?.purpose === PurposeType.Rationalization.code &&
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              onClick={handleOpenRationalizationSummaryModal}
            >
              {t('features.InvestmentScreening.Equipment.action.rationalizationSummary')}
            </Button>
          </Box>
        }
        {equipmentUsageType === EquipmentUsageType.GeneralPurpose.code &&
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              onClick={handleOpenGeneralPurposeChecklistModal}
            >
              {t('features.InvestmentScreening.Equipment.action.generalPurposeChecklist')}
            </Button>
          </Box>
        }
      </Flex>
      {/* 確認ボタン（2行目） */}
      <Flex justify="flex-end" mt="10px">
        <Box>
          <Button
            colorScheme="blue"
            name="companyDivision"
            onClick={handleModalOpen}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.Equipment.button.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="procurementDivision"
            onClick={handleModalOpen}
            isDisabled={!pagePermissions?.eabledProcurementDivision}
            ref={refs["procurementDivision"]}
          >
            {t('features.InvestmentScreening.Equipment.button.procurementDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
        </Box>
      </Flex>

      {/* 設備入力モーダル */}
      <Modal isOpen={isOpenInputModal} onClose={onCloseInputModal} closeOnOverlayClick={false} onCloseComplete={handleCloseCompleteInputModal} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="1783px" minW="720px">
          <ModalHeader>
            <fieldset disabled={!pagePermissions?.isEditable}>
              <HStack>
                <Text>{t('features.InvestmentScreening.Equipment.field.equipmentInput')}</Text>
                <HStack spacing={2}>
                  <Text>{t('features.InvestmentScreening.Equipment.field.rowCount')}</Text>
                  <NumberInput
                    value={equipmentCount}
                    onChange={(valueString) => handleEquipmentCount(valueString)}
                    width="80px" min={0} max={MAX_EQUIPMENT_COUNT}
                    isDisabled={inputMode === "google" || !pagePermissions?.isEditable}
                  >
                    <NumberInputField placeholder={t('features.InvestmentScreening.Equipment.field.equipmentCount')} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <HStack spacing={4}>
                    <ButtonGroup spacing={4} isAttached  >
                      <ReadOnlyExemptButton colorScheme={inputMode === "manual" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                        setInputMode("manual");
                        handleMaualClick();
                      }}>
                        {t('features.InvestmentScreening.Equipment.field.inputOnScreen')}
                      </ReadOnlyExemptButton>

                      <ReadOnlyExemptButton colorScheme={inputMode === "google" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                        setInputMode("google");
                        handleGoogleSpreadClick();
                      }}>
                        GoogleSpread
                      </ReadOnlyExemptButton>
                    </ButtonGroup>
                  </HStack>
                </HStack >
                <Button colorScheme="blue" onClick={handleSyncData} disabled={inputMode === "manual"} isLoading={isLoading}>
                  {t('features.InvestmentScreening.Equipment.field.dataLink')}
                </Button>
              </HStack >
            </fieldset>
          </ModalHeader >
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden">
            <fieldset disabled={!pagePermissions?.isEditable} style={{ minWidth: 0 }}>
              {clickedNode && (
                <Box mb={4}>
                  <HStack>
                    <Text fontWeight="bold" minWidth="163px" textAlign="right">{t('features.InvestmentScreening.Equipment.field.processName')}</Text>
                    <Text ml={4}>{clickedNode.uniqueProcessName}</Text>
                  </HStack>
                  {clickedNode.uniqueProcessCycleTimeInSecondByPartNoList && (
                    <HStack mt={2} alignItems="start" >
                      <TableContainer overflowX="auto" w="1720px">
                        <Table variant="simple" size="sm" style={{ width: `auto`, tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
                          <Tbody>
                            <Tr>
                              <Th width="180px" textAlign="right" fontWeight="bold" textColor="#000" style={{ borderWidth: "0px", fontSize: "16px" }} position="sticky"
                                left="0"
                                bg="white"
                                zIndex="1">
                                {t('features.InvestmentScreening.Equipment.field.modelPartNo')}
                              </Th>
                              {clickedNode.uniqueProcessCycleTimeInSecondByPartNoList.map((p, index) => (
                                <Td key={index} width="150px">
                                  <Text ml={4} whiteSpace="nowrap" textAlign="right" isTruncated={true} title={resolveDisplayPartNoByKey(p.partNo, partNo)}>
                                    {resolveDisplayPartNoByKey(p.partNo, partNo)}
                                  </Text>
                                </Td>
                              ))}
                            </Tr>
                            <Tr>
                              <Th width="180px" textAlign="right" fontWeight="bold" textColor="#000" style={{ borderWidth: "0px", fontSize: "16px" }} position="sticky"
                                left="0"
                                bg="white"
                                zIndex="1">
                                {t('features.InvestmentScreening.Equipment.field.itemNameLabel')}
                              </Th>
                              {clickedNode.uniqueProcessCycleTimeInSecondByPartNoList.map((p, index) => (
                                <Td key={index} width="150px">
                                  <Text ml={4} whiteSpace="nowrap" textAlign="right" isTruncated={true} title={`${p.itemName}`}>
                                    {p.itemName !== undefined ? `${p.itemName}` : "--"}
                                  </Text>
                                </Td>
                              ))}
                            </Tr>
                            <Tr>
                              <Th textAlign="right" fontWeight="bold" textColor="#000" style={{ borderWidth: "0px", fontSize: "16px" }} position="sticky"
                                left="0"
                                bg="white"
                                zIndex="1">
                                {t('features.InvestmentScreening.Equipment.field.cycleTime')}
                              </Th>
                              {clickedNode.uniqueProcessCycleTimeInSecondByPartNoList.map((p, index) => (
                                <Td key={index}>
                                  <Text ml={4} whiteSpace="nowrap" textAlign="right" isTruncated={true} title={`${p.cycleTime} ${t('features.InvestmentScreening.Equipment.field.seconds')}`}>
                                    {`${p.cycleTime} ${t('features.InvestmentScreening.Equipment.field.seconds')}`}
                                  </Text>
                                </Td>
                              ))}
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </HStack>
                  )}
                </Box>
              )}

              <Box mt={3} width="100%">
                <Text fontSize="lg" fontWeight="bold">
                  {t('features.InvestmentScreening.Equipment.field.spreadSheetContent')}
                </Text>
                <TableContainer border="0px solid" borderColor="gray.200" height="calc(90vh - 370px)" w="100%" overflowX="auto" overflowY="auto">
                  <Table variant="simple" size="sm" style={{ width: "3200px", tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className="mm-ceas-table">
                    <colgroup>
                      {projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? (<col width="38px" />) : ""}
                      <col width="300px" />
                      <col width="185px" />
                      <col width="150px" />
                      <col width="120px" />
                      <col width="150px" />
                      <col width="250px" />
                      <col width="250px" />
                      <col width="100px" />
                      <col width="185px" />
                      <col width="185px" />
                      <col width="185px" />
                      <col width="110px" />
                      <col width="110px" />
                      <col width="110px" />
                      <col width="100px" />
                      <col width="200px" />
                      <col width="200px" />
                    </colgroup>
                    <Thead position="sticky" top={0} bg="white" zIndex="10" >
                      <Tr >
                        {projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? (<Th rowSpan={2} position="sticky" left="0px" zIndex="1" bg="white"></Th>) : ""}
                        <Th style={{ ...inputModalHeaderStyle, borderRight: "1px solid #ccc" }} rowSpan={2}
                          position="sticky"
                          left={projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? "40px" : "0px"}
                          zIndex="1"
                          bg="white"
                        >{t('features.InvestmentScreening.Equipment.field.equipmentName')}
                        </Th>
                        <Th style={{ ...inputModalHeaderStyle, borderLeft: "none" }} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.unitPrice')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2} >
                          <Flex direction="column" align="center" justify="center" gap={1}>
                            <Text fontSize="16px" whiteSpace="nowrap">{t('features.InvestmentScreening.Equipment.field.currency')}</Text>
                            <CurrencyList currencyData={currencyData} buttonSize="xs" fontSize="10px" />
                          </Flex>
                        </Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.quantity')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.quantityUnit')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.investmentAmount')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.bookValue')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.depreciationYears')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.applicationMonth')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.inspectionMonth')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.depreciationStartMonth')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={1} colSpan={3}>
                          <Flex align="center" justify="center" gap={1}>
                            <Text fontSize="12px">{t('features.InvestmentScreening.Equipment.field.personnelPerShift')}</Text>
                            <HelpIcon size="20px" placement="bottom-start">
                              <Text>
                                {t('features.InvestmentScreening.Equipment.help.personnelDesc1')}<br />
                                {t('features.InvestmentScreening.Equipment.help.personnelDesc2')}<br />
                                {t('features.InvestmentScreening.Equipment.help.personnelDesc3')}<br />
                                {t('features.InvestmentScreening.Equipment.help.personnelDesc4')}
                              </Text>
                            </HelpIcon>
                          </Flex>
                        </Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2} whiteSpace="normal"><Text fontSize="12px">{t('features.InvestmentScreening.Equipment.field.personnelWithoutEquipPerShift')}</Text></Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.equipmentCategory')}</Th>
                        <Th style={inputModalHeaderStyle} rowSpan={2}>{t('features.InvestmentScreening.Equipment.field.supplier')}</Th>
                      </Tr>
                      <Tr>
                        <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('features.InvestmentScreening.Equipment.field.unskilledWorker')}</Text></Th>
                        <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('features.InvestmentScreening.Equipment.field.multiSkilledWorker')}</Text></Th>
                        <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('features.InvestmentScreening.Equipment.field.technician')}</Text></Th>
                      </Tr>
                    </Thead>
                    <Tbody zIndex="10">
                      {equipmentData.map((row, rowIndex: number) => {

                        if (equipmentCount <= rowIndex) {
                          return false;
                        }

                        return (
                          <Tr key={rowIndex} height="60px" background={row.isReadonly ?
                            "#eee" : isModifiedRow(row) && projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? "#fafffa" : "white"}>
                            {projectWithRdsData?.purpose !== PurposeType.NewProduct.code ?
                              (
                                <Td p={1} position="sticky" left="0px" zIndex="1"
                                  background={row.isReadonly ?
                                    "#eee" : isModifiedRow(row) && projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? "#fafffa" : "white"}>
                                  {row.isReadonly ? (
                                    // ＋ボタン
                                    <IconButton
                                      aria-label="Add Equipment"
                                      icon={<FaPlusCircle />}
                                      onClick={() => handleAddEquipment(row, rowIndex)}
                                      colorScheme="blue"
                                      isDisabled={isDisabledAddBranch(row.equipmentId!, equipmentData)}
                                      m={0}
                                      width="30px"
                                      minWidth="30px"
                                      height="30px"
                                      title={t('features.InvestmentScreening.Equipment.help.changeNote')}
                                    />) : (
                                    // -ボタン
                                    <IconButton
                                      aria-label="Remove PartNo."
                                      icon={<FaCircleMinus />}
                                      onClick={() => handleRemoveEquipment(row, rowIndex)}
                                      colorScheme="red"
                                      m={0}
                                      width="30px"
                                      minWidth="30px"
                                      height="30px"
                                      title={t('features.InvestmentScreening.Equipment.help.deleteNote')}
                                    />
                                  )}
                                </Td>
                              ) : ""}
                            {/* 設備名称 */}
                            <Td
                              style={{ borderRight: "1px solid #ccc" }}
                              position="sticky"
                              left={projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? "40px" : "0px"}
                              zIndex="1"
                              background={row.isReadonly ? "#eee" : isModifiedRow(row) && projectWithRdsData?.purpose !== PurposeType.NewProduct.code ? "#fafffa" : "white"}
                            >
                              <FormControl isInvalid={errorsList?.[rowIndex]?.equipmentName?.isError}>
                                {inputMode === "manual" && !row.isReadonly && !isModifiedRow(row) ? (
                                  <Input
                                    name="equipmentName" data-index={rowIndex} value={row.equipmentName ?? ""}
                                    onChange={handleChangeEquipment} bg="white"
                                  />
                                ) : (
                                  <Text style={inputModalTextStyle} title={row.equipmentName ?? ""}>{row.equipmentName}</Text>
                                )
                                }
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.equipmentName?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 単価 */}
                            <Tooltip label={row.invalidFields?.["unitPrice"]}
                              isDisabled={!row.invalidFields?.["unitPrice"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="120px" width="120px" bg={row.invalidFields?.["unitPrice"] ? "red.200" : undefined} style={{ borderLeft: "none" }}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.unitPrice?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_UNIT_PRICE} decimalPlaces={UNIT_PRICE_DECIMAL_PLACES} value={row.unitPrice ?? null}
                                      onChange={(value) => changeEquipment(rowIndex, "unitPrice", value)}
                                      bg={row.invalidFields?.["unitPrice"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["unitPrice"] ? undefined : formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}>
                                      {formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.unitPrice?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 通貨 */}
                            <Tooltip label={row.invalidFields?.["currencyId"]}
                              isDisabled={!row.invalidFields?.["currencyId"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="120px" width="120px" bg={row.invalidFields?.["currencyId"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.currencyId?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <Select
                                      value={row.currencyId || ""}
                                      onChange={(event) => {
                                        // 選択された通貨情報を設定
                                        const selectedCurrency = currencyData.find(currency => currency.currencyId === event.target.value);
                                        changeEquipment(rowIndex, "currencyId", selectedCurrency?.currencyId);
                                        changeEquipment(rowIndex, "currencyName", selectedCurrency?.currencyName);
                                      }}
                                      bg={row.invalidFields?.["currencyId"] ? "red.200" : "white"}
                                    >
                                      <option value="" disabled>

                                      </option>
                                      {currencyData.map(currency => (
                                        <option key={currency.currencyId} value={currency.currencyId}>
                                          {currency.currencyName}
                                        </option>
                                      ))}
                                    </Select>
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["currencyId"] ? undefined : row.currencyName ?? ""}>
                                      {row.currencyName ?? ""}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.currencyId?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 数量 */}
                            <Tooltip label={row.invalidFields?.["quantity"]}
                              isDisabled={!row.invalidFields?.["quantity"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="80px" width="80px" bg={row.invalidFields?.["quantity"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.quantity?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={
                                        projectWithRdsData?.purpose === PurposeType.NewProduct.code || projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code
                                          ? 1
                                          : isModifiedRow(row)
                                            ? -getBaseTotalQuantity(row, equipmentData)
                                            : 1
                                      }
                                      max={MAX_QUANTITY} value={row.quantity ?? null}
                                      decimalPlaces={0}
                                      onChange={(value) => changeEquipment(rowIndex, "quantity", value)}
                                      bg={row.invalidFields?.["quantity"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["quantity"] ? undefined : formatNumber(row.isReadonly ? (row.totalQuantity ?? row.quantity ?? 0) : (row.quantity || 0), "")}>
                                      {formatNumber(row.isReadonly ? (row.totalQuantity ?? row.quantity ?? 0) : (row.quantity || 0), "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.quantity?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 数量単位 */}
                            <Tooltip label={row.invalidFields?.["quantityUnit"]}
                              isDisabled={!row.invalidFields?.["quantityUnit"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td bg={row.invalidFields?.["quantityUnit"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.quantityUnit?.isError}>
                                  {inputMode === "manual" && !row.isReadonly && !isModifiedRow(row) ? (
                                  <Select
                                    isInvalid={!!row.invalidFields?.["quantityUnit"]}
                                    value={row.quantityUnit || "unit"}
                                    onChange={(value) => {handleUnitSelectChange(rowIndex, value.target.value)}}
                                  >
                                    <option value="unit">
                                      {t('features.InvestmentScreening.Equipment.options.quantityUnit.unit')}
                                    </option>
                                    <option value="set">
                                      {t('features.InvestmentScreening.Equipment.options.quantityUnit.set')}
                                    </option>
                                    <option value="pcs">
                                      {t('features.InvestmentScreening.Equipment.options.quantityUnit.pcs')}
                                    </option>

                                    {/* 4. 自由入力されたカスタム単位の描画処理 */}
                                    {(() => {
                                      // 1. 標準選択肢（固定値）の定義
                                      const standardUnits = ["unit", "set", "pcs"];

                                      // 2. 重複を排除しながらカスタム選択肢を保持するSetオブジェクト
                                      const customOptions = new Set<string>();

                                      // ① 初期データ（DBから取得した全行）から標準以外の単位を追加
                                      unitSnap?.forEach((item) => {
                                        if (item.quantityUnit && !standardUnits.includes(item.quantityUnit)) {
                                          customOptions.add(item.quantityUnit);
                                        }
                                      });

                                      // ② 自由入力モーダルで追加された全行の単位を追加
                                      Object.values(customUnits || {}).flat().forEach((unit) => {
                                        if (unit && !standardUnits.includes(unit)) {
                                          customOptions.add(unit);
                                        }
                                      });

                                      // ③ 画面上の全行（equipmentData）で現在選択されている標準以外の単位を追加
                                      equipmentData?.forEach((eq) => {
                                        if (eq.quantityUnit && !standardUnits.includes(eq.quantityUnit)) {
                                          customOptions.add(eq.quantityUnit);
                                        }
                                      });

                                      // 3. 配列に変換して <option> を生成
                                      return Array.from(customOptions).map((unit) => (
                                        <option key={unit} value={unit}>
                                          {unit}
                                        </option>
                                      ));
                                    })()}
                                    
                                    <option value="custom">
                                      {t('features.InvestmentScreening.Equipment.options.quantityUnit.custom')}
                                    </option>
                                  </Select>) : row.quantityUnit === null || row.quantityUnit === undefined ? (
                                    // null または undefined の場合：デフォルト値（unit）を表示
                                    <Text style={inputModalTextStyle}>
                                      {t('features.InvestmentScreening.Equipment.options.quantityUnit.unit')}
                                    </Text>
                                  ) : row.quantityUnit === "" ? null : (
                                    // 値が入っている場合：その値を表示
                                    <Text style={inputModalTextStyle}>{toSpreadsheetUnitLabel(row.quantityUnit)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.quantityUnit?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 金額 */}
                            <Td maxWidth="150px" width="150px">
                              <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(calcAmount(row.unitPrice, row.quantity, row) || 0, row.currencyName ?? "")}>
                                {formatNumber(calcAmount(row.unitPrice, row.quantity, row) || 0, row.currencyName ?? "")}
                              </Text>
                            </Td>
                            {/* 簿価 TODO:入力フィールドに変更 */}
                            <Td>
                              <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}>
                                {formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}
                              </Text>
                            </Td>
                            {/* 償却年数 */}
                            <Tooltip label={row.invalidFields?.["depreciationPeriod"]}
                              isDisabled={!row.invalidFields?.["depreciationPeriod"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="80px" width="80px" bg={row.invalidFields?.["depreciationPeriod"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.depreciationPeriod?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_DEPRECIATION_PERIOD} value={row.depreciationPeriod ?? null}
                                      decimalPlaces={0}
                                      onChange={(value) => changeEquipment(rowIndex, "depreciationPeriod", value)}
                                      bg={row.invalidFields?.["depreciationPeriod"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["depreciationPeriod"] ? undefined : formatNumber(row.depreciationPeriod || 0, "")}>
                                      {formatNumber(row.depreciationPeriod || 0, "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.depreciationPeriod?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 申請予定月 */}
                            <Tooltip label={row.invalidFields?.["applicationPlannedMonth"]}
                              isDisabled={!row.invalidFields?.["applicationPlannedMonth"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="80px" width="80px" bg={row.invalidFields?.["applicationPlannedMonth"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.applicationPlannedMonth?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <Input
                                      type="month"
                                      name="applicationPlannedMonth"
                                      value={row.applicationPlannedMonth ?? ""}
                                      onChange={(event) => {
                                        if (event.target.value.length > 7) {
                                          return;
                                        }
                                        changeEquipment(rowIndex, "applicationPlannedMonth", event.target.value)
                                      }}
                                      bg={row.invalidFields?.["applicationPlannedMonth"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} title={row.invalidFields?.["applicationPlannedMonth"] ? undefined : row.applicationPlannedMonth ?? ""}>{formatYearMonthByLocale(row.applicationPlannedMonth)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.applicationPlannedMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 検収月 */}
                            <Tooltip label={row.invalidFields?.["acceptanceMonth"]}
                              isDisabled={!row.invalidFields?.["acceptanceMonth"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="80px" width="80px" bg={row.invalidFields?.["acceptanceMonth"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.acceptanceMonth?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <Input
                                      type="month"
                                      name="acceptanceMonth"
                                      value={row.acceptanceMonth ?? ""}
                                      onChange={(event) => {
                                        if (event.target.value.length > 7) {
                                          return;
                                        }
                                        changeEquipment(rowIndex, "acceptanceMonth", event.target.value)
                                      }}
                                      bg={row.invalidFields?.["acceptanceMonth"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} title={row.invalidFields?.["acceptanceMonth"] ? undefined : row.acceptanceMonth ?? ""}>{formatYearMonthByLocale(row.acceptanceMonth)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.acceptanceMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 償却開始月 */}
                            <Tooltip label={row.invalidFields?.["depreciationStartMonth"]}
                              isDisabled={!row.invalidFields?.["depreciationStartMonth"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="80px" width="80px" bg={row.invalidFields?.["depreciationStartMonth"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.depreciationStartMonth?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <Input
                                      type="month"
                                      name="depreciationStartMonth"
                                      value={row.depreciationStartMonth ?? ""}
                                      onChange={(event) => {
                                        if (event.target.value.length > 7) {
                                          return;
                                        }
                                        changeEquipment(rowIndex, "depreciationStartMonth", event.target.value)
                                      }}
                                      bg={row.invalidFields?.["depreciationStartMonth"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} title={row.invalidFields?.["depreciationStartMonth"] ? undefined : row.depreciationStartMonth ?? ""}>{formatYearMonthByLocale(row.depreciationStartMonth)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.depreciationStartMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 設備に必要な人員数 - 単純工 */}
                            <Tooltip label={row.invalidFields?.["unskilledWorker"]}
                              isDisabled={!row.invalidFields?.["unskilledWorker"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td bg={row.invalidFields?.["unskilledWorker"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.unskilledWorker?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_NUMBER_OF_PEOPLE_REQUIRED} decimalPlaces={2} value={row.unskilledWorker ?? 0}
                                      onChange={(value) => changeEquipment(rowIndex, "unskilledWorker", value)}
                                      bg={row.invalidFields?.["unskilledWorker"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["unskilledWorker"] ? undefined : formatNumber(new Decimal(row.unskilledWorker || 0), "")}>
                                      {formatNumber(new Decimal(row.unskilledWorker || 0), "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.unskilledWorker?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 設備に必要な人員数 - 多能工 */}
                            <Tooltip label={row.invalidFields?.["multiSkilledWorker"]}
                              isDisabled={!row.invalidFields?.["multiSkilledWorker"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td bg={row.invalidFields?.["multiSkilledWorker"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.multiSkilledWorker?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_NUMBER_OF_PEOPLE_REQUIRED} decimalPlaces={2} value={row.multiSkilledWorker ?? 0}
                                      onChange={(value) => changeEquipment(rowIndex, "multiSkilledWorker", value)}
                                      bg={row.invalidFields?.["multiSkilledWorker"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["multiSkilledWorker"] ? undefined : formatNumber(new Decimal(row.multiSkilledWorker || 0), "")}>
                                      {formatNumber(new Decimal(row.multiSkilledWorker || 0), "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.multiSkilledWorker?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 設備に必要な人員数 - テクニシャン */}
                            <Tooltip label={row.invalidFields?.["technician"]}
                              isDisabled={!row.invalidFields?.["technician"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td bg={row.invalidFields?.["technician"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.technician?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_NUMBER_OF_PEOPLE_REQUIRED} decimalPlaces={2} value={row.technician ?? 0}
                                      onChange={(value) => changeEquipment(rowIndex, "technician", value)}
                                      bg={row.invalidFields?.["technician"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["technician"] ? undefined : formatNumber(new Decimal(row.technician || 0), "")}>
                                      {formatNumber(new Decimal(row.technician || 0), "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.technician?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 設備なし時に必要な人員数 */}
                            <Tooltip label={row.invalidFields?.["numberOfPeopleRequiredForWithoutEquipment"]}
                              isDisabled={!row.invalidFields?.["numberOfPeopleRequiredForWithoutEquipment"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td bg={row.invalidFields?.["numberOfPeopleRequiredForWithoutEquipment"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.numberOfPeopleRequiredForWithoutEquipment?.isError}>
                                  {inputMode === "manual" && !row.isReadonly ? (
                                    <CustomNumberInput
                                      min={0} max={MAX_NUMBER_OF_PEOPLE_REQUIRED} decimalPlaces={2} value={row.numberOfPeopleRequiredForWithoutEquipment ?? 0}
                                      onChange={(value) => changeEquipment(rowIndex, "numberOfPeopleRequiredForWithoutEquipment", value)}
                                      bg={row.invalidFields?.["numberOfPeopleRequiredForWithoutEquipment"] ? "red.200" : "white"}
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right" title={row.invalidFields?.["numberOfPeopleRequiredForWithoutEquipment"] ? undefined : formatNumber(new Decimal(row.numberOfPeopleRequiredForWithoutEquipment || 0), "")}>
                                      {formatNumber(new Decimal(row.numberOfPeopleRequiredForWithoutEquipment || 0), "")}
                                    </Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.numberOfPeopleRequiredForWithoutEquipment?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 設備分類 */}
                            <Tooltip label={row.invalidFields?.["equipmentGroupId"]}
                              isDisabled={!row.invalidFields?.["equipmentGroupId"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="120px" width="120px" bg={row.invalidFields?.["equipmentGroupId"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.equipmentGroupId?.isError}>
                                  {row.equipmentGroupName ? (
                                    <Button variant="ghost" p={0} title={row.invalidFields?.["equipmentGroupId"] ? undefined : getMultilingualDisplayName(row.equipmentGroupName)} cursor="pointer"
                                      isDisabled={row.isReadonly}
                                      onClick={() => {
                                        setSelectedIndex(rowIndex);
                                        onOpenGroupModal();
                                      }}>
                                      <Text style={inputModalButtonTextStyle} textAlign="left">
                                        {getMultilingualDisplayName(row.equipmentGroupName)}
                                        {equipmentGroups.find(g => g.equipmentGroupId === row.equipmentGroupId)?.isCandidate && (
                                          <Box as="span" color="orange.500" fontSize="xs" ml={1}>{t('features.InvestmentScreening.Equipment.field.candidate')}</Box>
                                        )}
                                      </Text>
                                    </Button>
                                  ) : (
                                    <Button colorScheme="blue"
                                      isDisabled={row.isReadonly}
                                      onClick={() => {
                                        setSelectedIndex(rowIndex);
                                        onOpenGroupModal();
                                      }}>
                                      {t('features.InvestmentScreening.Equipment.field.equipmentCategory')}
                                    </Button>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.equipmentGroupId?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            {/* 設備サプライヤー */}
                            <Tooltip label={row.invalidFields?.["equipmentSupplierId"]}
                              isDisabled={!row.invalidFields?.["equipmentSupplierId"]}
                              hasArrow
                              bg="red.500"
                              color="white"
                              placement="top"
                              fontSize="md"
                              padding={2}>
                              <Td maxWidth="120px" width="120px" bg={row.invalidFields?.["equipmentSupplierId"] ? "red.200" : undefined}>
                                <FormControl isInvalid={errorsList?.[rowIndex]?.equipmentSupplierId?.isError}>
                                  {row.equipmentSupplierName ? (
                                    <Button variant="ghost" p={0} title={row.invalidFields?.["equipmentSupplierId"] ? undefined : getMultilingualDisplayName(row.equipmentSupplierName)} cursor="pointer"
                                      isDisabled={row.isReadonly}
                                      onClick={() => {
                                        setSelectedIndex(rowIndex);
                                        onOpenSupplierModal();
                                      }}>
                                      <Text style={inputModalButtonTextStyle} textAlign="left">
                                        {getMultilingualDisplayName(row.equipmentSupplierName)}
                                        {equipmentSuppliers.find(s => s.equipmentSupplierId === row.equipmentSupplierId)?.isCandidate && (
                                          <Box as="span" color="orange.500" fontSize="xs" ml={1}>{t('features.InvestmentScreening.Equipment.field.candidate')}</Box>
                                        )}
                                      </Text>
                                    </Button>
                                  ) : (
                                    <Button colorScheme="teal"
                                      isDisabled={row.isReadonly}
                                      onClick={() => {
                                        setSelectedIndex(rowIndex);
                                        onOpenSupplierModal();
                                      }}>
                                      {t('features.InvestmentScreening.Equipment.field.supplier')}
                                    </Button>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.equipmentSupplierId?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                          </Tr>
                        )
                      }
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </fieldset>
          </ModalBody>
          <ModalFooter>
            {pagePermissions?.isEditable && (
              <>
                <Button
                  colorScheme="teal"
                  variant="outline"
                  onClick={handleTempSaveInputModal}
                  m={1}
                >
                  {t('features.InvestmentScreening.Equipment.button.tempSave')}
                </Button>
                <Button
                  colorScheme="teal"
                  variant="outline"
                  onClick={handleTempLoadInputModal}
                  m={1}
                >
                  {t('features.InvestmentScreening.Equipment.button.tempLoad')}
                </Button>
              </>
            )}
            <Button colorScheme="blue" isDisabled={!pagePermissions?.isEditable || isSaving} isLoading={isSaving} onClick={handleSaveAll} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={onCloseInputModal} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent >
      </Modal >

      {/* 設備サプライヤー選択モーダル */}
      <EquipmentSupplierSelectModal
        isOpen={isOpenSupplierModal}
        onClose={onCloseSupplierModal}
        equipmentSuppliers={equipmentSuppliers}
        onSelect={handleSelectSupplier}
        onRegisterCandidate={handleRegisterSupplierCandidate}
      />

      {/* 設備分類選択モーダル */}
      <EquipmentGroupSelectModal
        isOpen={isOpenGroupModal}
        onClose={onCloseGroupModal}
        equipmentGroups={equipmentGroups}
        equipmentCategories={equipmentCategories}
        onSelect={handleSelectGroup}
        onRegisterCandidate={handleRegisterCandidate}
      />

      {/* チェックリスト一覧モーダル */}
      <Modal isOpen={isOpenProductListModal} onClose={onCloseProductListModal} closeOnOverlayClick={false} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="1783px" minW="720px" maxH="90vh">
          <ModalHeader>
            <Text>{t('features.InvestmentScreening.Equipment.modal.checkListTitle')}</Text>
          </ModalHeader >
          <ModalCloseButton />
          <ModalBody overflow="hidden">
            <Text display={"flex"} justifyContent={"flex-start"} ml={5}>
              {t('features.InvestmentScreening.Equipment.modal.checkListCount', { count: equipmentListData.length ?? 0 })}
            </Text>
            <Box display="flex" justifyContent="flex-start" h={"calc(90vh - 180px)"} mt="10px" px="10px">
              <Box width="100%">
                <TableContainer border="1px solid" borderColor="gray.200" height="100%" overflowY="auto" overflowX="auto">
                  <Table border="1px solid" borderColor="gray.200" variant="simple" style={{ tableLayout: 'fixed' }}>
                    <Thead position="sticky" top={0} zIndex={10} backgroundColor="gray.100" boxShadow={`0 -1px 0 #EEEEEE`}>
                      <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"10%"}>{t('features.InvestmentScreening.Equipment.field.equipmentName')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"10%"}>{t('features.InvestmentScreening.Equipment.field.unitPrice')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"6%"}>{t('features.InvestmentScreening.Equipment.field.currency')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"8%"}>{t('features.InvestmentScreening.Equipment.field.quantity')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"7%"}>{t('features.InvestmentScreening.Equipment.field.quantityUnit')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"10%"}>{t('features.InvestmentScreening.Equipment.field.investmentAmount')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"10%"}>{t('features.InvestmentScreening.Equipment.field.bookValue')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"14%"}>{t('features.InvestmentScreening.Equipment.field.estimateChecklist')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w={"14%"}>{t('features.InvestmentScreening.Equipment.field.tenChecklistLabel')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {equipmentListData.map((row, rowIndex: number) => {

                        const equipmentId = row.equipmentId ?? "";
                        const hasEstimateCheckList = estimateCheckListData[equipmentId]?.hasEstimateCheckList ?? false;
                        const checkList = checkListData[equipmentId]?.checkList ?? null;
                        const checkListUpdateDate = checkListData[equipmentId]?.updateDate ?? null;

                        return (

                          <Tr key={rowIndex} _hover={{ bg: "gray.200" }}>
                            {/* 設備名称 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <FormControl isInvalid={errorsList?.[rowIndex]?.equipmentName?.isError}>
                                <Text style={inputModalTextStyle} title={row.equipmentName ?? ""}>{row.equipmentName}</Text>
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.equipmentName?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 単価 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <FormControl isInvalid={errorsList?.[rowIndex]?.unitPrice?.isError}>
                                <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}>
                                  {formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}
                                </Text>
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.unitPrice?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 通貨 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <FormControl isInvalid={errorsList?.[rowIndex]?.currencyId?.isError}>
                                <Text style={inputModalTextStyle} textAlign="right" title={row.currencyName ?? ""}>
                                  {row.currencyName ?? ""}
                                </Text>
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.currencyId?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 数量 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <FormControl isInvalid={errorsList?.[rowIndex]?.quantity?.isError}>
                                <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(row.quantity || 0, "")}>
                                  {formatNumber(row.quantity || 0, "")}
                                </Text>
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.quantity?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 数量単位 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <FormControl isInvalid={errorsList?.[rowIndex]?.quantity?.isError}>
                                <Text style={inputModalTextStyle} textAlign="right" title={toDbQuantityUnitCode(row.quantityUnit)}>
                                  {toSpreadsheetUnitLabel(row.quantityUnit)}
                                </Text>
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList?.[rowIndex]?.quantity?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>
                            {/* 金額 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(calcAmount(row.unitPrice, row.quantity, row) || 0, row.currencyName ?? "")}>
                                {formatNumber(calcAmount(row.unitPrice, row.quantity, row) || 0, row.currencyName ?? "")}
                              </Text>
                            </Td>
                            {/* 簿価 */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <Text style={inputModalTextStyle} textAlign="right" title={formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}>
                                {formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}
                              </Text>
                            </Td>
                            {/* 見積チェックリスト */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <EstimateCheckList
                                  projectId={projectId}
                                  equipmentId={row.equipmentId}
                                  equipmentName={row.equipmentName}
                                  pagePermissions={pagePermissions}
                                  setCheckListUpdateStatus={setCheckListUpdateStatus}
                                  currencyData={currencyData}
                                />
                                <Icon
                                  as={FaCheckCircle}
                                  boxSize={8}
                                  mr={2}
                                  color={!hasEstimateCheckList ? '#ccc' : 'green'}
                                />
                              </Box>
                            </Td>
                            {/* １０か条チェックリスト */}
                            <Td borderRight="1px solid" borderColor="gray.200" style={cellStyle}>
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <CheckList projectId={projectId} equipmentId={equipmentId} checkList={checkList} setCheckListDataList={setCheckListData} updateDate={checkListUpdateDate} pagePermissions={pagePermissions} masterList={checklistMasterList} />
                                <Icon
                                  as={FaCheckCircle}
                                  boxSize={8}
                                  mr={2}
                                  color={!checkList ? '#ccc' : 'green'}
                                />
                              </Box>
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => {
              onCloseProductListModal();
            }} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent >
      </Modal >

      {/* 設備一覧 */}
      <EquipmentListModal
        isOpen={isOpenEquipmentListModal}
        onClose={onCloseEquipmentListModal}
        projectId={projectId}
        equipmentListData={equipmentListData}
        equipmentGroups={equipmentGroups}
        equipmentSuppliers={equipmentSuppliers}
        uniqueProcessData={uniqueProcessData}
        calcAmount={calcAmount}
        pagePermissions={pagePermissions}
        onEditClick={async () => {
          await getEquipmentEditData();
          onOpenEquipmentEditModal();
        }}
      />

      {/* 設備編集 */}
      <EquipmentEditModal
        isOpen={isOpenEquipmentEditModal}
        onClose={onCloseEquipmentEditModal}
        projectId={projectId}
        equipmentListData={equipmentEditData}
        equipmentCategories={equipmentCategories}
        equipmentGroups={equipmentGroups}
        equipmentSuppliers={equipmentSuppliers}
        uniqueProcessData={uniqueProcessData}
        currencyData={currencyData}
        calcAmount={calcAmount}
        pagePermissions={pagePermissions}
        purpose={projectWithRdsData?.purpose ?? undefined}
        onSaveComplete={async () => {
          await getEquipmentInfoList();
          await getEquipmentEditData();
          await drawProcessFlowWithFetch();
        }}
        onListClick={onOpenEquipmentListModal}
        onRegisterCandidate={handleRegisterCandidate}
        onRegisterSupplierCandidate={handleRegisterSupplierCandidate}
        reviewDate={reviewDate}
      />

      {/* 過去ベストベンチマークモーダル */}
      <ProductionCapacityBenchmarkModal
        isOpen={isOpenBenchmarkModal}
        onClose={onCloseBenchmarkModal}
        benchmarkData={benchmarkData}
        onUpdate={handleSaveBenchmarkData}
        equipmentData={equipmentListData.filter(eq => !eq.isReadonly)}
        productionLineData={productionLineDataList}
        processCodeData={uniqueProcessData}
        projectId={projectId}
        pagePermissions={pagePermissions}
        currencyName={currencyData.find(c => c.currencyId === getLocalCurrencyId(projectWithRdsData?.companyPlantSnapshot))?.currencyName}
        partNoData={partNo.filter((p): p is { partNo: string; displayPartNo?: string; quantityAndPrice?: string | null } => !!p.partNo)}
      />

      {/* 更新前後サマリーモーダル */}
      <UpdateSummaryModal
        isOpen={isOpenUpdateSummaryModal}
        onClose={onCloseUpdateSummaryModal}
        onUpdate={handleSaveUpdateSummaryData}
        equipmentData={equipmentListData.filter(eq => !eq.isReadonly)}
        processCodeData={uniqueProcessData}
        projectId={projectId}
        pagePermissions={pagePermissions}
        projectWithRdsData={projectWithRdsData ?? undefined}
        partNoStrings={partNo.map((p: any) => p.partNo).filter(Boolean)}
        partNoData={partNo.filter((p): p is { partNo: string; displayPartNo?: string } => !!p.partNo)}
        reviewDate={reviewDate}
        currencyData={currencyData}
        localCurrency={projectWithRdsData?.currencyId}
        isSimpleMode={isSimpleApplicationForm(projectWithRdsData?.purpose, projectWithRdsData?.applicationFormType)}
      />

      {/* 合理化前後サマリーモーダル */}
      <RationalizationSummaryModal
        isOpen={isOpenRationalizationSummaryModal}
        onClose={onCloseRationalizationSummaryModal}
        onUpdate={handleSaveRationalizationSummaryData}
        equipmentData={equipmentListData.filter(eq => !eq.isReadonly)}
        processCodeData={uniqueProcessData}
        projectId={projectId}
        pagePermissions={pagePermissions}
        projectStartDate={projectWithRdsData?.projectStartDate}
        projectEndDate={projectWithRdsData?.projectEndDate}
        partNoData={partNo.filter((p): p is { partNo: string; displayPartNo?: string } => !!p.partNo)}
        currencyData={currencyData}
        localCurrency={projectWithRdsData?.currencyId}
        reviewDate={reviewDate}
        isSimpleMode={isSimpleApplicationForm(projectWithRdsData?.purpose, projectWithRdsData?.applicationFormType)}
      />

      {/* 汎用設備チェックリストモーダル */}
      <GeneralPurposeChecklistModal
        isOpen={isOpenGeneralPurposeChecklistModal}
        onClose={onCloseGeneralPurposeChecklistModal}
        onUpdate={handleSaveGeneralPurposeChecklistData}
        projectId={projectId}
        pagePermissions={pagePermissions}
        purpose={projectWithRdsData?.purpose}
      />

      {/* 画像アップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenImageUploadModal}
        onClose={onCloseImageUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"EquipmentImages"}
        allowedExtensions={allowedImageExtensions}
      />

      {/* ファイルアップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenFileUploadModal}
        onClose={onCloseFileUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"EquipmentFiles"}
        allowedExtensions={allowedExtensions}
      />

      {/* 確認モーダル */}
      < Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="xl" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.Equipment.modal.confirmTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t('features.InvestmentScreening.Equipment.modal.confirmMessage')}</Text>
            <Table size="lg">
              <Tr>
                <Th>{t('features.InvestmentScreening.Equipment.modal.maxCycleTime')}</Th>
                <Td>{
                  productionLine?.cycleTime !== null && productionLine?.cycleTime !== undefined
                    ? productionLine.cycleTime + ` ${t('features.InvestmentScreening.Equipment.field.seconds')}` : t('features.InvestmentScreening.Equipment.modal.notSet')
                }</Td>
              </Tr>
              <Tr>
                <Th>{t('features.InvestmentScreening.Equipment.modal.yieldRate')}</Th>
                <Td>{productionLine.yieldRate} %</Td>
              </Tr>
              {projectWithRdsData?.purpose === PurposeType.IncreasedProduction.code && (
                <Tr>
                  <Th>{t('features.InvestmentScreening.Equipment.modal.bestProductionQuantity')}</Th>
                  <Td>{
                    productionLine?.bestProductionQuantity !== null && productionLine?.bestProductionQuantity !== undefined
                      ? formatNumber(Number(productionLine.bestProductionQuantity) || 0, "") + ` ${t('features.InvestmentScreening.Equipment.modal.pieces')}` : ""
                  }</Td>
                </Tr>
              )}
              <Tr>
                <Th>{t('features.InvestmentScreening.Equipment.modal.totalInvestmentAmount')}</Th>
                <Td>{formatNumber(equipmentSumAmount || 0, localCurrencyName)} {localCurrencyName}</Td>
              </Tr>
              <Tr>
                <Th>{t('features.InvestmentScreening.Equipment.modal.totalPersonnel')}</Th>
                <Td>{formatNumber(sumNumberOfPeopleRequiredForEquipment || 0, "")}</Td>
              </Tr>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} isDisabled={isSaving} isLoading={isSaving} onClick={(e) => handleConfirm(e)}>
              {t('common.button.confirm')}
            </Button>
            <Button variant="ghost" onClick={onCloseConfirmModal}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* 自由入力モーダル */}
      <Modal isOpen={isCustomUnitModalOpen} onClose={handleCustomUnitCancel} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.Equipment.options.customUnitModal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('features.InvestmentScreening.Equipment.options.customUnitModal.unit')}</FormLabel>
                <Input
                  size="sm"
                  value={customUnit}
                  maxLength={15}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  placeholder={t('features.InvestmentScreening.Equipment.options.customUnitModal.placeholder')}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              size="sm"
              mr={2}
              isDisabled={!customUnit.trim()}
              onClick={handleCustomUnitConfirm}
            >
              {t('components.RationalizationSummaryInputPopover.button.confirm')}
            </Button>
            <Button size="sm" onClick={handleCustomUnitCancel}>
              {t('components.RationalizationSummaryInputPopover.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box >
  );
};

export default Equipment;


/**
  * 階層構造構築
  * @param uniqueProcessData プロセス情報
  */
export const buildHierarchyProcess = (uniqueProcessData: UniqueProcessType[]) => {
  if (!uniqueProcessData || uniqueProcessData.length === 0) {
    return {} as UniqueProcessType;
  }

  // シーケンスでソート
  uniqueProcessData.sort((a, b) => a.uniqueProcessSeq - b.uniqueProcessSeq);

  // レベルの最小値を取得
  const rootProcess = uniqueProcessData.reduce((min, current) => {
    return (current.uniqueLevel ?? Number.MAX_VALUE) < (min.uniqueLevel ?? Number.MAX_VALUE) ? current : min;
  });

  const firstIndex = uniqueProcessData.indexOf(rootProcess) + 1;
  buildSubProcess(uniqueProcessData, firstIndex, rootProcess);

  return rootProcess;
}

/**
 * 階層構造構築
 * @param uniqueProcessData プロセス情報
 * @param firstIndex 開始位置
 * @param parent 親プロセス
 * @returns 再帰処理からの復帰位置
 */
export const buildSubProcess = (uniqueProcessData: UniqueProcessType[], firstIndex: number, parent: UniqueProcessType | null) => {
  let currentProcess: UniqueProcessType | null = null;
  const parentLevel = parent?.uniqueLevel ?? Number.MAX_VALUE;

  let i = firstIndex || 0;
  for (i = firstIndex; i < uniqueProcessData.length; i++) {

    const subProcess: UniqueProcessType = uniqueProcessData[i];
    const subLevel = subProcess.uniqueLevel ?? Number.MAX_VALUE;

    if (subLevel === (parentLevel + 1)) {
      // 子プロセス（１つ下のレベル）
      if (parent) {
        if (!parent.subProcess) {
          parent.subProcess = [];
        }
        parent.subProcess.push(subProcess);
      }
      currentProcess = subProcess;

    } else if (subLevel === (parentLevel + 2)) {
      // 子プロセス ⇒ 孫プロセス（２つ下のレベル）
      i = buildSubProcess(uniqueProcessData, i, currentProcess) ?? 0;
    } else {
      // １つ上のレベルへ戻る
      return i - 1;
    }
  }

  return i;
}
